import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppQuickNav from '../components/AppQuickNav';   // 🧭 швидкий перехід по розділах вкладки
import {
  Box, Container, Typography, Paper, Button, Chip, Stack, Alert,
  CircularProgress, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Avatar, Slider, Divider, Switch, TextField, FormControlLabel,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab,
} from '@mui/material';
import API, { getApiKeys, saveApiKey } from '../api';

const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` });
const JSON_AUTH = () => ({ ...AUTH(), 'Content-Type': 'application/json' });
// Ціни гаманця. Порядок і підписи — те, як про ці числа думає власник,
// а не як вони називаються в базі.
const ZQ_CREDIT_FIELDS = [
  ['credits_per_eur', 'Токенів за €1'],
  ['cost_item', '💎 «Коротко» — ціна тлумачення'],
  ['starter_credits', 'Новому юзеру подарувати'],
  ['cost_syn', '💞 Сумісність — ціна пункту'],
  ['ref_credits', 'За приведеного друга'],
  ['ref_welcome_credits', 'Новачку за запрошенням'],
  ['ref_bonus_days', 'Днів преміуму за друга (0 — не давати)'],
  ['tier2_mult', '📖 «Докладно» — у скільки разів дорожче'],
  ['tier3_mult', '📚 «Глибоко» — у скільки разів дорожче'],
];
const ZQ_CREDIT_REASONS = {
  starter: 'стартовий баланс', topup: 'поповнення', map_item: 'пункт карти',
  syn_item: 'розбір на двох', admin_grant: 'нарахування вручну',
  admin_take: 'списання вручну', referral: 'за приведеного друга',
  referral_welcome: 'бонус за запрошенням',
};
const ZQ_ITEMS_FULL = 99;   // пунктів карти разом із ведичним варіантом
// Скільки коштує НАМ одна відповідь. Два поля, а не одне: різниця між
// моделями тут не у відсотках, а в порядок величини, тож усереднене число
// брехало б на більшості рядків. Дробові — перевіряються окремо від цін.
const ZQ_AI_FIELDS = [
  ['ai_cost_mini_eur', 'Вартість 1 відповіді, €'],
  ['ai_cost_full_eur', 'Вартість 1 глибокої відповіді, €'],
];

const PREM_FEATURES = [
  ['natal', '🌌 Натальна карта'],
  ['chat', '💬 AI-астролог (чат)'],
  ['forecast', '🔮 Глибокі прогнози (тиждень/місяць/рік)'],
  ['compat', '❤️ Сумісність'],
];
// Вкладки сторінки ZODIAQ: секції згруповано за темою, щоб не гортати один
// суцільний список на ~1900 рядків. Порядок УСЕРЕДИНІ вкладки лишається за
// перетягуванням (⠿ / _zqOrd / localStorage) — тут керуємо тільки видимістю.
// Щоб перенести секцію в іншу вкладку — досить пересунути її id у цьому списку.
const ZQ_TABS = [
  // Доходи стоять ПЕРШИМИ навмисно: це те, заради чого решта існує.
  { label: '\u{1F4B0} Доходи', ids: ['s15'] },
  { label: '\u{1F4CA} Огляд', ids: ['s1', 's2'] },
  // newUsers: число НЕ з notifications-counts, а з самого списку юзерів
  // (newCount ← /dashboard/zodiaq/users). Гасне по кліку на юзера.
  { label: '\u{1F465} Юзери', ids: ['s13', 's17', 's0'], newUsers: true },
  { label: '\u{1F48E} Монетизація', ids: ['s9', 's10', 's14'] },
  { label: '\u{1F9D1}\u200D\u{1F3EB} Консультації', ids: ['s11'] },
  { label: '\u{1F3A8} Оформлення', ids: ['s3', 's4', 's16', 's5', 's6', 's7'] },
  // badge: ключ із /api/dashboard/notifications-counts. Позначка в бічному
  // меню каже «щось є», але не каже ДЕ — тож дублюємо число на вкладці.
  { label: '\u{1F6E0} Підтримка', ids: ['s12'], badge: 'zodiaq_support' },
  { label: '\u2699\uFE0F Налаштування', ids: ['secret', 's8'] },
];

// 💌 04.10.2026: запрошення «Поділитися ботом» — мови в порядку застосунку
const INV_LANGS = [
  ['uk', 'UA'], ['ru', 'RU'], ['en', 'EN'], ['es', 'ES'],
  ['de', 'DE'], ['it', 'IT'], ['fr', 'FR'], ['pl', 'PL'],
];
// Картинки ZODIAQ — лише через проксі дашборду: CSP сайту (img-src) не пускає
// zodiaq.mediabot.uk, тож <img src="https://zodiaq…"> тут мовчки не показувався.
function ZqAuthImg({ src, style, fallback }) {
  const [url, setUrl] = useState(null);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    let alive = true; let ou = null;
    setBad(false); setUrl(null);
    fetch(src, { headers: AUTH() })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((b) => { if (!alive) return; ou = URL.createObjectURL(b); setUrl(ou); })
      .catch(() => { if (alive) setBad(true); });
    return () => { alive = false; if (ou) URL.revokeObjectURL(ou); };
  }, [src]);
  if (bad) return fallback || null;
  if (!url) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={18} /></Box>;
  return <img src={url} alt="" style={style} />;
}

// 👥 Хто кого привів і скільки на цьому заробив (05.10.2026, прохання OWNER).
// Суми — з книги нарахувань, а не з прапорців: «оплачено» означає, що токени справді нараховано.
const ZqRefPairs_STATUS = {
  paid: ['оплачено', '#2e5a32'], pending: ['ще не нараховано', '#444'],
  flagged: ['⚠️ підозра — вирішити нижче', '#7a3a2a'], rejected: ['відхилено', '#555'],
};
function ZqRefPairs({ summary, pairs, totals, unit, pendingHint }) {
  const [open, setOpen] = useState(false);
  const sm = summary || [], pr = pairs || [];
  const dt = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('.') : '');
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{ color: '#ddd', fontWeight: 700, fontSize: 14, mb: .5 }}>👥 Хто кого привів</Typography>
      {pr.length === 0 ? <Typography sx={{ color: '#777', fontSize: 13 }}>Поки ніхто нікого не привів.</Typography> : (<>
        <Typography sx={{ color: '#999', fontSize: 12.5, mb: 1 }}>
          Усього: <b style={{ color: '#ddd' }}>{(totals && totals.pairs) ?? pr.length}</b> запрошених ·
          запрошувачам нараховано <b style={{ color: '#ddd' }}>{(totals && totals.earned) ?? 0} {unit}</b> ·
          новачкам <b style={{ color: '#ddd' }}>{(totals && totals.invited_bonus) ?? 0} {unit}</b>
        </Typography>
        <Box sx={{ border: '1px solid rgba(150,130,255,.18)', borderRadius: 1.5, overflow: 'hidden', mb: 1 }}>
          <Box sx={{ display: 'flex', px: 1.2, py: .6, bgcolor: 'rgba(124,92,255,.12)', fontSize: 12, color: '#aaa', gap: 1 }}>
            <Box sx={{ flex: 1 }}>Хто запрошував</Box><Box sx={{ width: 90, textAlign: 'right' }}>Привів</Box><Box sx={{ width: 110, textAlign: 'right' }}>Заробив</Box>
          </Box>
          {sm.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', px: 1.2, py: .6, borderTop: '1px solid rgba(150,130,255,.1)', fontSize: 13, color: '#ddd', gap: 1 }}>
              <Box sx={{ flex: 1, wordBreak: 'break-word' }}>{s.referrer_name || s.referrer_tid}</Box>
              <Box sx={{ width: 90, textAlign: 'right' }}>{s.invited}</Box>
              <Box sx={{ width: 110, textAlign: 'right', color: s.earned > 0 ? '#39e58c' : '#888', fontWeight: 600 }}>+{s.earned} {unit}</Box>
            </Box>
          ))}
        </Box>
        <Button size="small" onClick={() => setOpen(!open)} sx={{ color: '#9fd', fontSize: 12, px: 0 }}>
          {open ? '▲ Сховати' : '▼ Показати'} усі запрошення ({pr.length})
        </Button>
        {open && pr.map((p, i) => {
          const st = ZqRefPairs_STATUS[p.status] || ZqRefPairs_STATUS.pending;
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: .6, borderBottom: '1px solid rgba(150,130,255,.1)', flexWrap: 'wrap' }}>
              <Typography sx={{ color: '#888', fontSize: 12, width: 72 }}>{dt(p.created_at)}</Typography>
              <Typography sx={{ color: '#ddd', fontSize: 13, flex: 1, minWidth: 180 }}>{p.referrer_name || p.referrer_tid} → <b>{p.invited_name || p.invited_tid}</b></Typography>
              <Typography sx={{ color: p.inviter_amount > 0 ? '#39e58c' : '#888', fontSize: 12.5 }}>запрошувачу +{p.inviter_amount}</Typography>
              <Typography sx={{ color: p.invited_amount > 0 ? '#39e58c' : '#888', fontSize: 12.5 }}>· новачку +{p.invited_amount}</Typography>
              <Chip size="small" label={st[0]} sx={{ fontSize: 10.5, height: 20, color: '#fff', bgcolor: st[1] }} />
            </Box>);
        })}
        {pendingHint && <Typography sx={{ color: '#777', fontSize: 11.5, mt: .8 }}>{pendingHint}</Typography>}
      </>)}
    </Box>
  );
}

function ZqRefSection() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { const r = await fetch('/api/dashboard/zodiaq/referrals', { headers: AUTH() }); const j = await r.json();
      if (j && j.ok) setD(j); else setErr('Не вдалося завантажити'); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (err) return <Typography sx={{ color: '#ff6b6b', fontSize: 13 }}>❌ {err}</Typography>;
  if (!d) return <CircularProgress size={18} />;
  return <ZqRefPairs summary={d.summary} pairs={d.referrals} totals={d.totals} unit="💎"
    pendingHint="«Ще не нараховано» — друг прийшов, але ще не відкрив гаманець чи карту: токени за нього нараховуються саме тоді. Дні Преміуму за старою схемою тут не видно (вони не пишуться в книгу)." />;
}

const SND_SLOTS = [
  ['wind', '🌀 Ураган (закручується вихор)'],
  ['whoosh', '💨 Свист (картку відносить)'],
  ['door', '🚪 Розчинення дверей'],
  ['dive', '🕳️ Пірнання у сферу'],
  ['reveal', '✨ Дзвіночок (екран знаку)'],
];

// 💎 2026-08-14: «активний» = був у застосунку за останні 7 днів;
// «тихий» = зайшов і зник (<=1 активний день) і зареєстрований понад 2 дні тому.
const _zqDaysAgo = (d) => {
  if (!d) return null;
  const t = new Date(d);
  if (isNaN(t)) return null;
  return Math.floor((Date.now() - t.getTime()) / 86400000);
};
const _zqIsActive7 = (u) => { const d = _zqDaysAgo(u.last_visit); return d != null && d <= 7; };
const _zqIsQuiet = (u) => {
  const reg = _zqDaysAgo(u.first_seen);
  return (u.active_days ?? 0) <= 1 && reg != null && reg >= 2;
};

export default function ZodiaqPage() {
  // 💎 Секрет адмінки Zodiaq (2026-08-13): збережений тут -> одразу діє на
  // проксі-виклики дашборду до Zodiaq, без рестарту чи ручного .env.
  const [zqKeys, setZqKeys] = useState([]);
  const [zqTokenInput, setZqTokenInput] = useState('');
  const [zqMsg, setZqMsg] = useState('');
  const [zqLink, setZqLink] = useState('');
  const loadZqKeys = () => getApiKeys().then(r => setZqKeys(r.data || [])).catch(() => {});
  const zqMasked = () => { const k = zqKeys.find(x => x.service === 'zodiaq_admin_token'); return k && k.masked ? k.masked : ''; };
  const saveZqToken = async () => {
    if (!zqTokenInput.trim()) { setZqMsg('❌ Спершу встав токен'); return; }
    try { await saveApiKey('zodiaq_admin_token', zqTokenInput.trim()); setZqMsg('✅ Збережено'); setZqTokenInput(''); loadZqKeys(); }
    catch { setZqMsg('❌ Помилка збереження'); }
  };
  const openZqAdmin = async () => {
    try { const r = await API.get('/dashboard/zodiaq/admin-link'); window.open(r.data.url, '_blank'); }
    catch { setZqMsg('❌ Не вдалось отримати посилання'); }
  };
  useEffect(() => { loadZqKeys(); }, []);
  const [users, setUsers] = useState([]);
  // 💎 2026-08-14: фільтр за КОРИСТУВАННЯМ (не за доступом)
  const [uFilter, setUFilter] = useState('all');
  const [count, setCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [logoMsg, setLogoMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [welMsg, setWelMsg] = useState('');
  const [welUploading, setWelUploading] = useState(false);
  const welRef = useRef(null);

  // ── 💌 Запрошення «Поділитися ботом» (s16) ──
  // Тексти й картинка живуть у ZODIAQ; порожнє поле = стандартний текст.
  const [inv, setInv] = useState(null);
  const [invLang, setInvLang] = useState('uk');
  const [invEdit, setInvEdit] = useState({ caption: {}, btn: {} });
  const [invMsg, setInvMsg] = useState('');
  const [shInvBusy, setShInvBusy] = useState(false);
  const [invImgT, setInvImgT] = useState(Date.now());
  const loadInv = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/share-invite', { headers: AUTH() });
      const d = await r.json();
      if (d && d.ok) { setInv(d); setInvEdit({ caption: { ...d.caption }, btn: { ...d.btn } }); }
      else setInvMsg('❌ Не вдалося завантажити налаштування запрошення.');
    } catch (e) { setInvMsg('❌ ZODIAQ недоступний: ' + e.message); }
  }, []);
  useEffect(() => { loadInv(); }, [loadInv]);
  const invDirty = !!inv && INV_LANGS.some(([l]) =>
    (invEdit.caption[l] || '') !== (inv.caption[l] || '') || (invEdit.btn[l] || '') !== (inv.btn[l] || ''));
  const setInvField = (kind, val) => setInvEdit((p) => ({ ...p, [kind]: { ...p[kind], [invLang]: val } }));
  const saveInv = async () => {
    setShInvBusy(true); setInvMsg('');
    try {
      const r = await fetch('/api/dashboard/zodiaq/share-invite', {
        method: 'POST', headers: { ...AUTH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption: invEdit.caption, btn: invEdit.btn }) });
      const d = await r.json();
      if (d && d.ok) { setInvMsg('✅ Збережено — наступні запрошення підуть уже з цим текстом.'); await loadInv(); }
      else setInvMsg('❌ ' + ((d && d.detail) || 'Не збережено'));
    } catch (e) { setInvMsg('❌ Помилка: ' + e.message); }
    setShInvBusy(false);
  };
  // scope = код мови (картинка лише для неї) або 'all' (спільна для всіх мов)
  const invScopeName = (scope) => (scope === 'all' ? 'усіх мов' : ((INV_LANGS.find(([l]) => l === scope) || [])[1] || scope));
  const uploadInvImg = async (e, scope) => {
    const input = e.target;
    const f = input.files && input.files[0];
    if (!f) return;
    setShInvBusy(true); setInvMsg('');
    try {
      const fd = new FormData(); fd.append('file', f); fd.append('scope', scope);
      const r = await fetch('/api/dashboard/zodiaq/share-invite/image', { method: 'POST', headers: AUTH(), body: fd });
      const d = await r.json();
      if (d && d.ok) { setInvMsg('✅ Картинку для ' + invScopeName(scope) + ' завантажено.'); setInvImgT(Date.now()); await loadInv(); }
      else setInvMsg('❌ ' + ((d && d.detail) === 'not_image' ? 'Це не картинка.' : 'Не завантажено.'));
    } catch (err) { setInvMsg('❌ Помилка: ' + err.message); }
    setShInvBusy(false);
    input.value = '';
  };
  const removeInvImg = async (scope) => {
    const next = scope === 'all' ? 'мови без своєї картинки отримають намальовану картку'
      : (inv && inv.has_image ? 'для ' + invScopeName(scope) + ' буде спільна картинка' : 'для ' + invScopeName(scope) + ' буде намальована картка');
    if (!window.confirm('Прибрати картинку для ' + invScopeName(scope) + '? Після цього ' + next + '.')) return;
    setShInvBusy(true); setInvMsg('');
    try {
      const r = await fetch('/api/dashboard/zodiaq/share-invite/image?scope=' + scope, { method: 'DELETE', headers: AUTH() });
      const d = await r.json();
      if (d && d.ok) { setInvMsg('✅ Картинку для ' + invScopeName(scope) + ' прибрано.'); setInvImgT(Date.now()); await loadInv(); }
      else setInvMsg('❌ Не вдалося.');
    } catch (e) { setInvMsg('❌ Помилка: ' + e.message); }
    setShInvBusy(false);
  };

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetch('/api/dashboard/zodiaq/users', { headers: AUTH() });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      setUsers(d.users || []); setCount(d.count || 0); setNewCount(d.new_count || 0);
    } catch (e) { setErr('Не вдалося завантажити список: ' + e.message); }
    setLoading(false);
  }, []);

  const markSeen = useCallback(async (tid, all) => {
    try {
      await fetch('/api/dashboard/zodiaq/user-seen',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify(all ? { all: true } : { tid: String(tid) }) });
    } catch (e) { /* тихо */ }
    setUsers(us => us.map(u => (all || u.telegram_id === tid) ? { ...u, is_new: false } : u));
    setNewCount(c => all ? 0 : Math.max(0, c - 1));
  }, []);

  const [stats, setStats] = useState(null);
  const loadStats = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/stats', { headers: AUTH() });
      const d = await r.json();
      if (d && d.users) setStats(d);
    } catch (e) { /* тихо */ }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  // ── Крос-промо: запросити юзерів інших продуктів у ZODIAQ ──
  const [invAud, setInvAud] = useState('pl');
  const [invCounts, setInvCounts] = useState(null);
  const [invBusy, setInvBusy] = useState(false);
  const [invResult, setInvResult] = useState('');
  const [invUsers, setInvUsers] = useState([]);       // [{tid,name,lang}]
  const [invSel, setInvSel] = useState(() => new Set()); // обрані tid
  const [invListBusy, setInvListBusy] = useState(false);
  const loadInvCounts = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/invite/audiences', { headers: AUTH() });
      setInvCounts(await r.json());
    } catch (e) { /* тихо */ }
  }, []);
  useEffect(() => { loadInvCounts(); }, [loadInvCounts]);
  const loadInvList = useCallback(async (aud) => {
    setInvListBusy(true); setInvUsers([]); setInvSel(new Set());
    try {
      const r = await fetch('/api/dashboard/zodiaq/invite/list?audience=' + aud, { headers: AUTH() });
      const d = await r.json();
      const us = (d && d.users) || [];
      setInvUsers(us);
      setInvSel(new Set(us.map(u => u.tid)));   // за замовч. усі обрані
    } catch (e) { /* тихо */ }
    setInvListBusy(false);
  }, []);
  const pickAudience = (a) => { setInvAud(a); setInvResult(''); loadInvList(a); };
  useEffect(() => { loadInvList(invAud); /* перший рендер */ // eslint-disable-next-line
  }, [loadInvList]);
  const toggleInv = (tid) => setInvSel(prev => {
    const n = new Set(prev); if (n.has(tid)) n.delete(tid); else n.add(tid); return n;
  });
  const invAll = () => setInvSel(new Set(invUsers.map(u => u.tid)));
  const invNone = () => setInvSel(new Set());
  const sendInvite = async () => {
    const ids = Array.from(invSel);
    if (!ids.length) { setInvResult('⚠️ Не обрано жодного юзера'); return; }
    if (!window.confirm('Розіслати запрошення в ZODIAQ ' + ids.length + ' обраним юзерам (' + invAud.toUpperCase() + ')? Дію не скасувати.')) return;
    setInvBusy(true); setInvResult('⏳ Розсилаю…');
    try {
      const r = await fetch('/api/dashboard/zodiaq/invite/send',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ audience: invAud, ids }) });
      const d = await r.json();
      if (r.ok && typeof d.sent === 'number') setInvResult('✅ Надіслано: ' + d.sent + ' · не доставлено: ' + d.failed + ' · всього: ' + d.total);
      else setInvResult('⚠️ Помилка розсилки');
    } catch (e) { setInvResult('⚠️ Помилка мережі'); }
    setInvBusy(false);
    loadInvCounts();
  };

  const [events, setEvents] = useState(null);
  const loadEvents = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/events', { headers: AUTH() });
      const d = await r.json();
      if (d && d.events) setEvents(d);
    } catch (e) { /* тихо */ }
  }, []);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  const toggleUserProtect = async (tid, enabled) => {
    try {
      await fetch('/api/dashboard/zodiaq/user-protect', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ tid: String(tid), enabled }) });
      setUsers(us => us.map(u => u.telegram_id === tid ? { ...u, screen_protection: enabled } : u));
      setUserDetail(d => d && d.telegram_id === tid ? { ...d, screen_protection: enabled } : d);
    } catch (e) { /* тихо */ }
  };

  // ── Філософія дня (власні фрази) ──
  const [philList, setPhilList] = useState([]);
  const [philText, setPhilText] = useState('');
  const [philAuthor, setPhilAuthor] = useState('');
  const [philEditId, setPhilEditId] = useState(null);
  const [philMsg, setPhilMsg] = useState('');
  const loadPhil = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/philosophy', { headers: AUTH() });
      const d = await r.json();
      setPhilList((d && d.phrases) || []);
    } catch (e) { /* тихо */ }
  }, []);
  useEffect(() => { loadPhil(); }, [loadPhil]);
  const savePhil = async () => {
    const text = philText.trim(); if (!text) return;
    setPhilMsg('…');
    try {
      const body = { text, author: philAuthor.trim() };
      if (philEditId) body.id = philEditId;
      const r = await fetch('/api/dashboard/zodiaq/philosophy', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify(body) });
      const d = await r.json();
      if (r.ok && d.ok) { setPhilMsg('✅ Збережено.'); setPhilText(''); setPhilAuthor(''); setPhilEditId(null); await loadPhil(); }
      else setPhilMsg('❌ Помилка.');
    } catch (e) { setPhilMsg('❌ ' + e.message); }
  };
  const editPhil = (p) => { setPhilEditId(p.id); setPhilText(p.text || ''); setPhilAuthor(p.author || ''); };
  const deletePhil = async (id) => {
    try { await fetch('/api/dashboard/zodiaq/philosophy/' + id, { method: 'DELETE', headers: AUTH() }); await loadPhil(); if (philEditId === id) { setPhilEditId(null); setPhilText(''); setPhilAuthor(''); } } catch (e) { /* тихо */ }
  };

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setUploading(true); setLogoMsg('');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await fetch('/api/dashboard/zodiaq/logo', { method: 'POST', headers: AUTH(), body: fd });
      const d = await r.json();
      if (r.ok && d.ok) setLogoMsg('✅ Лого завантажено — з\'явиться в центрі кулі (перевідкрий Mini App).');
      else setLogoMsg('❌ Помилка завантаження.');
    } catch (e) { setLogoMsg('❌ Помилка: ' + e.message); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };
  const uploadWelcome = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setWelUploading(true); setWelMsg('');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await fetch('/api/dashboard/zodiaq/welcome-image', { method: 'POST', headers: AUTH(), body: fd });
      const d = await r.json();
      if (r.ok && d.ok) setWelMsg('✅ Вітальне фото завантажено — юзери побачать його у вітанні (/start).');
      else setWelMsg('❌ Помилка завантаження.');
    } catch (e) { setWelMsg('❌ Помилка: ' + e.message); }
    setWelUploading(false);
    if (welRef.current) welRef.current.value = '';
  };

  // ── Звуки-ефекти + фонова музика + гучність ──
  const [cfg, setCfg] = useState({ sounds: {}, music: [], music_volume: 50 });
  const [tracks, setTracks] = useState([]);
  const [vol, setVol] = useState(50);
  const [mediaMsg, setMediaMsg] = useState('');
  const [busy, setBusy] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const playAudio = async (key, srcUrl) => {
    try {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (playingId === key) { setPlayingId(null); return; }   // повторний клік = стоп
      setPlayingId(key);
      const r = await fetch(srcUrl, { headers: AUTH() });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const url = URL.createObjectURL(await r.blob());
      const a = new Audio(url); audioRef.current = a;
      a.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); };
      a.play();
    } catch (e) { setPlayingId(null); }
  };

  const loadMedia = useCallback(async () => {
    try {
      const [rc, rm] = await Promise.all([
        fetch('/api/dashboard/zodiaq/config', { headers: AUTH() }),
        fetch('/api/dashboard/zodiaq/music', { headers: AUTH() }),
      ]);
      const c = await rc.json(); const m = await rm.json();
      setCfg(c || { sounds: {} }); setVol((c && c.music_volume) || 50); setTracks((m && m.tracks) || []);
    } catch (e) { /* тихо */ }
  }, []);
  useEffect(() => { loadMedia(); }, [loadMedia]);

  const uploadSound = async (slot, e) => {
    const f = e.target.files && e.target.files[0]; if (!f) return;
    setBusy('snd_' + slot); setMediaMsg('');
    try {
      const fd = new FormData(); fd.append('slot', slot); fd.append('file', f);
      const r = await fetch('/api/dashboard/zodiaq/sound', { method: 'POST', headers: AUTH(), body: fd });
      const d = await r.json();
      setMediaMsg(r.ok && d.ok ? '✅ Звук збережено (перевідкрий Mini App).' : '❌ Помилка звуку.');
      await loadMedia();
    } catch (err) { setMediaMsg('❌ ' + err.message); }
    setBusy(''); e.target.value = '';
  };
  const resetSound = async (slot) => {
    setBusy('snd_' + slot);
    try { await fetch('/api/dashboard/zodiaq/sound?slot=' + slot, { method: 'DELETE', headers: AUTH() }); await loadMedia(); } catch (e) { /* тихо */ }
    setBusy('');
  };
  const uploadMusic = async (e) => {
    const f = e.target.files && e.target.files[0]; if (!f) return;
    setBusy('music'); setMediaMsg('');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await fetch('/api/dashboard/zodiaq/music', { method: 'POST', headers: AUTH(), body: fd });
      const d = await r.json();
      setMediaMsg(r.ok && d.ok ? '✅ Трек додано.' : '❌ Помилка треку.');
      await loadMedia();
    } catch (err) { setMediaMsg('❌ ' + err.message); }
    setBusy(''); e.target.value = '';
  };
  const deleteMusic = async (id) => {
    try { await fetch('/api/dashboard/zodiaq/music/' + id, { method: 'DELETE', headers: AUTH() }); await loadMedia(); } catch (e) { /* тихо */ }
  };
  const [nameEdits, setNameEdits] = useState({});
  const renameMusic = async (id, name) => {
    setBusy('rename' + id);
    try {
      const fd = new FormData(); fd.append('name', name);
      await fetch('/api/dashboard/zodiaq/music/' + id + '/name', { method: 'POST', headers: AUTH(), body: fd });
      setMediaMsg('✅ Назву композиції збережено.');
      setNameEdits(s => { const n = { ...s }; delete n[id]; return n; });
      await loadMedia();
    } catch (e) { setMediaMsg('❌ ' + e.message); }
    setBusy('');
  };
  const saveVolume = async (v) => {
    setVol(v);
    try { const fd = new FormData(); fd.append('volume', v); await fetch('/api/dashboard/zodiaq/music-volume', { method: 'POST', headers: AUTH(), body: fd }); } catch (e) { /* тихо */ }
  };

  // ── МОНЕТИЗАЦІЯ (преміум) ──
  const [prem, setPrem] = useState(null);        // {enabled, features, tariffs, requisites, methods}
  const [payments, setPayments] = useState([]);
  const [premMsg, setPremMsg] = useState('');
  const [premBusy, setPremBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);  // {pid, url} для перегляду чека

  const loadPrem = useCallback(async () => {
    try {
      const [rc, rp] = await Promise.all([
        fetch('/api/dashboard/zodiaq/premium/config', { headers: AUTH() }),
        fetch('/api/dashboard/zodiaq/premium/payments', { headers: AUTH() }),
      ]);
      const c = await rc.json(); const p = await rp.json();
      setPrem(c && c.features ? c : { enabled: false, features: {}, tariffs: [], requisites: {}, methods: [] });
      setPayments((p && p.payments) || []);
    } catch (e) { /* тихо */ }
  }, []);
  useEffect(() => { loadPrem(); }, [loadPrem]);

  const savePrem = async (patch, note) => {
    setPremBusy(true); setPremMsg('');
    try {
      const r = await fetch('/api/dashboard/zodiaq/premium/config',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify(patch) });
      const d = await r.json();
      if (r.ok && d.ok) { setPremMsg('✅ ' + (note || 'Збережено.')); await loadPrem(); }
      else setPremMsg('❌ Помилка збереження.');
    } catch (e) { setPremMsg('❌ ' + e.message); }
    setPremBusy(false);
  };

  const toggleEnabled = () => savePrem({ enabled: !prem.enabled },
    !prem.enabled ? 'Преміум увімкнено.' : 'Преміум вимкнено (усе безкоштовно).');
  const toggleFeature = (f) => {
    const features = { ...prem.features, [f]: !prem.features[f] };
    setPrem({ ...prem, features });
    savePrem({ features }, 'Фічі оновлено.');
  };
  const toggleMethod = (m) => {
    const has = (prem.methods || []).includes(m);
    const methods = has ? prem.methods.filter(x => x !== m) : [...(prem.methods || []), m];
    setPrem({ ...prem, methods });
    savePrem({ methods }, 'Методи оплати оновлено.');
  };

  // Тарифи (локальне редагування → зберегти кнопкою)
  const setTariff = (i, key, val) => {
    const tariffs = prem.tariffs.map((t, j) => j === i ? { ...t, [key]: val } : t);
    setPrem({ ...prem, tariffs });
  };
  const addTariff = () => {
    const id = 'm' + Date.now().toString().slice(-5);
    setPrem({ ...prem, tariffs: [...(prem.tariffs || []), { id, months: 1, price: 0, currency: 'USD', enabled: true }] });
  };
  const removeTariff = (i) => setPrem({ ...prem, tariffs: prem.tariffs.filter((_, j) => j !== i) });
  const saveTariffs = () => {
    const tariffs = prem.tariffs.map(t => ({
      id: String(t.id), months: parseInt(t.months) || 1, price: Number(t.price) || 0,
      currency: (t.currency || 'USD').slice(0, 8), enabled: t.enabled !== false,
    }));
    savePrem({ tariffs }, 'Тарифи збережено.');
  };

  // Реквізити
  const setReq = (key, val) => setPrem({ ...prem, requisites: { ...(prem.requisites || {}), [key]: val } });
  const saveReq = () => savePrem({ requisites: prem.requisites || {} }, 'Реквізити збережено.');

  // Реферальна програма
  const toggleRef = () => {
    const r = { ...(prem.referral || {}), enabled: !(prem.referral && prem.referral.enabled) };
    setPrem({ ...prem, referral: r });
    savePrem({ ref_enabled: r.enabled }, 'Реферали оновлено.');
  };
  const setRefDays = (v) => setPrem({ ...prem, referral: { ...(prem.referral || {}), bonus_days: v } });
  const saveRefDays = () => savePrem({ ref_bonus_days: parseInt((prem.referral || {}).bonus_days) || 0 }, 'Бонус збережено.');
  const toggleScreenProtect = () => {
    const v = !(prem.screen_protection);
    setPrem({ ...prem, screen_protection: v });
    savePrem({ screen_protection: v }, v ? 'Захист від скріна увімкнено.' : 'Захист від скріна вимкнено.');
  };

  const viewReceipt = async (pid) => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/premium/receipt/' + pid + '/img', { headers: AUTH() });
      if (!r.ok) { setPremMsg('❌ Чек недоступний.'); return; }
      const url = URL.createObjectURL(await r.blob());
      setReceipt({ pid, url });
    } catch (e) { setPremMsg('❌ ' + e.message); }
  };
  const decide = async (pid, approve, months) => {
    setPremBusy(true); setPremMsg('');
    try {
      const url = '/api/dashboard/zodiaq/premium/' + (approve ? 'approve' : 'reject');
      const body = approve ? { pid, months } : { pid, reason: '' };
      const r = await fetch(url, { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify(body) });
      const d = await r.json();
      if (r.ok && d.ok) { setPremMsg(approve ? '✅ Преміум надано, юзер сповіщений.' : '✅ Заявку відхилено.'); await loadPrem(); }
      else setPremMsg('❌ Помилка.');
    } catch (e) { setPremMsg('❌ ' + e.message); }
    setPremBusy(false);
  };

  const deletePay = async (pid) => {
    if (!window.confirm('Видалити цю заявку зі списку?')) return;
    setPremBusy(true); setPremMsg('');
    try {
      const r = await fetch('/api/dashboard/zodiaq/premium/delete', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ pid }) });
      const d = await r.json();
      if (r.ok && d.ok) { setPremMsg('🗑 Заявку видалено.'); await loadPrem(); }
      else setPremMsg('❌ Помилка.');
    } catch (e) { setPremMsg('❌ ' + e.message); }
    setPremBusy(false);
  };

  // ── КОНСУЛЬТАЦІЯ З ЖИВИМ АСТРОЛОГОМ ──
  const [consEnabled, setConsEnabled] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [consReqs, setConsReqs] = useState([]);
  const [consMsg, setConsMsg] = useState('');
  const [consBusy, setConsBusy] = useState(false);
  const [consForm, setConsForm] = useState(null); // {id?, name, spec, price, bio, active} під час редагування
  const [consPhoto, setConsPhoto] = useState(null); // File
  const [consReceipt, setConsReceipt] = useState(null); // {rid, url}
  const [consPkgs, setConsPkgs] = useState([]);
  const [consPremMin, setConsPremMin] = useState(30);
  const [consPkgForm, setConsPkgForm] = useState(null); // {id?, name, minutes, price, active}
  const loadConsult = useCallback(async () => {
    try {
      const [rc, rr, rp] = await Promise.all([
        fetch('/api/dashboard/zodiaq/consultants', { headers: AUTH() }),
        fetch('/api/dashboard/zodiaq/consult/requests', { headers: AUTH() }),
        fetch('/api/dashboard/zodiaq/consult/packages', { headers: AUTH() }),
      ]);
      const c = await rc.json(); const q = await rr.json(); const p = await rp.json();
      setConsEnabled(!!(c && c.enabled));
      setConsultants((c && c.consultants) || []);
      setConsReqs((q && q.requests) || []);
      setConsPkgs((p && p.packages) || []);
      if (p && p.premium_minutes) setConsPremMin(p.premium_minutes);
      const [ra, rt] = await Promise.all([
        fetch('/api/dashboard/zodiaq/consult/applications', { headers: AUTH() }),
        fetch('/api/dashboard/zodiaq/consult/apply-terms', { headers: AUTH() }),
      ]);
      const a = await ra.json(); const t = await rt.json();
      setConsApps((a && a.applications) || []);
      if (t && t.terms != null) setApplyTerms({ terms: t.terms, commission: t.commission, enabled: t.enabled });
      const rpay = await fetch('/api/dashboard/zodiaq/consult/payouts', { headers: AUTH() });
      const pay = await rpay.json();
      setPayouts((pay && pay.payouts) || []);
      const rviol = await fetch('/api/dashboard/zodiaq/consult/violations', { headers: AUTH() });
      const viol = await rviol.json();
      setViolations((viol && viol.violations) || []); setViolUnseen((viol && viol.unseen) || 0);
    } catch (e) { /* тихо */ }
  }, []);
  useEffect(() => { loadConsult(); }, [loadConsult]);
  const [payouts, setPayouts] = useState([]);
  const [violations, setViolations] = useState([]);
  const [violUnseen, setViolUnseen] = useState(0);
  const markViolationsSeen = async () => {
    try {
      await fetch('/api/dashboard/zodiaq/consult/violations/seen', { method: 'POST', headers: JSON_AUTH(), body: '{}' });
      await loadConsult();
    } catch (e) { /* тихо */ }
  };
  const markPayout = async (id, paid) => {
    try {
      await fetch('/api/dashboard/zodiaq/consult/payout/mark', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ id, paid }) });
      setConsMsg(paid ? '✅ Позначено як виплачено.' : '↩️ Повернено в «до виплати».'); await loadConsult();
    } catch (e) { setConsMsg('❌ ' + e.message); }
  };
  const deletePayout = async (id) => {
    if (!window.confirm('Прибрати цю консультацію зі списку виплат?')) return;
    try {
      await fetch('/api/dashboard/zodiaq/consult/payout/delete', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ id }) });
      setConsMsg('🗑 Прибрано зі списку виплат.'); await loadConsult();
    } catch (e) { setConsMsg('❌ ' + e.message); }
  };
  const [consApps, setConsApps] = useState([]);
  const [applyTerms, setApplyTerms] = useState({ terms: '', commission: 10, enabled: true });
  const [appView, setAppView] = useState(null);   // заявка у деталях
  const [appPhoto, setAppPhoto] = useState(null);  // url фото заявки
  // пакети редагованого консультанта
  const [editPkgs, setEditPkgs] = useState([]);
  const [pkgRow, setPkgRow] = useState(null);
  const loadEditPkgs = async (cid) => {
    try { const r = await fetch('/api/dashboard/zodiaq/consult/packages?consultant_id=' + cid, { headers: AUTH() }); const d = await r.json(); setEditPkgs((d && d.packages) || []); }
    catch (e) { setEditPkgs([]); }
  };
  const CURRENCIES = ['USD', 'EUR', 'UAH', 'USDT', 'USDC'];
  const saveEditPkg = async (cid) => {
    if (!pkgRow) return;
    const body = { name: (pkgRow.minutes || 30) + ' хв', minutes: parseInt(pkgRow.minutes) || 30,
      price: pkgRow.price || '', currency: pkgRow.currency || 'USD', active: pkgRow.active !== false, consultant_id: cid };
    if (pkgRow.id) body.id = pkgRow.id;
    await fetch('/api/dashboard/zodiaq/consult/package', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify(body) });
    setPkgRow(null); await loadEditPkgs(cid);
  };
  const delEditPkg = async (id, cid) => {
    await fetch('/api/dashboard/zodiaq/consult/package/delete', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ id }) });
    await loadEditPkgs(cid);
  };
  const saveApplyTerms = async () => {
    try {
      await fetch('/api/dashboard/zodiaq/consult/apply-terms', { method: 'POST', headers: JSON_AUTH(),
        body: JSON.stringify({ terms: applyTerms.terms, commission: parseInt(applyTerms.commission) || 10, enabled: applyTerms.enabled }) });
      setConsMsg('✅ Умови збережено.');
    } catch (e) { setConsMsg('❌ ' + e.message); }
  };
  const viewAppPhoto = async (aid) => {
    try { const r = await fetch('/api/dashboard/zodiaq/consult/application-photo/' + aid, { headers: AUTH() });
      if (r.ok) setAppPhoto(URL.createObjectURL(await r.blob())); } catch (e) { /* тихо */ }
  };
  const decideApp = async (aid, approve, reason) => {
    setConsBusy(true); setConsMsg('');
    try {
      const url = '/api/dashboard/zodiaq/consult/application/' + (approve ? 'approve' : 'reject');
      const r = await fetch(url, { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ id: aid, reason: reason || '' }) });
      const d = await r.json();
      if (r.ok && d.ok) { setConsMsg(approve ? '✅ Прийнято — прихована картка створена, доопрацюй і ввімкни.' : '✅ Заявку відхилено.'); setAppView(null); await loadConsult(); }
      else setConsMsg('❌ Помилка.');
    } catch (e) { setConsMsg('❌ ' + e.message); }
    setConsBusy(false);
  };
  const deleteApp = async (aid) => {
    if (!window.confirm('Видалити заявку? Якщо вона була прийнята — видалиться і створений консультант (пакети, заявки, чат).')) return;
    setConsBusy(true); setConsMsg('');
    try {
      const r = await fetch('/api/dashboard/zodiaq/consult/application/delete', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ id: aid }) });
      const d = await r.json();
      if (r.ok && d.ok) { setConsMsg('✅ Заявку видалено (з усім ланцюгом).'); setAppView(null); await loadConsult(); }
      else setConsMsg('❌ Помилка.');
    } catch (e) { setConsMsg('❌ ' + e.message); }
    setConsBusy(false);
  };

  const savePkg = async () => {
    if (!consPkgForm) return;
    setConsBusy(true); setConsMsg('');
    try {
      const body = { name: consPkgForm.name || '', minutes: parseInt(consPkgForm.minutes) || 30,
        price: consPkgForm.price || '', active: consPkgForm.active !== false, sort: consPkgForm.sort || 0 };
      if (consPkgForm.id) body.id = consPkgForm.id;
      const r = await fetch('/api/dashboard/zodiaq/consult/package', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify(body) });
      const d = await r.json();
      if (r.ok && d.ok) { setConsMsg('✅ Пакет збережено.'); setConsPkgForm(null); await loadConsult(); }
      else setConsMsg('❌ Помилка збереження пакета.');
    } catch (e) { setConsMsg('❌ ' + e.message); }
    setConsBusy(false);
  };
  const deletePkg = async (id) => {
    if (!window.confirm('Видалити пакет?')) return;
    try { await fetch('/api/dashboard/zodiaq/consult/package/delete', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ id }) }); await loadConsult(); } catch (e) { /* тихо */ }
  };
  const savePremMin = async () => {
    try {
      await fetch('/api/dashboard/zodiaq/consult/premium-minutes', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ minutes: parseInt(consPremMin) || 30 }) });
      setConsMsg('✅ Ліміт Преміум збережено.');
    } catch (e) { setConsMsg('❌ ' + e.message); }
  };

  const toggleConsult = async () => {
    const v = !consEnabled; setConsEnabled(v);
    try {
      await fetch('/api/dashboard/zodiaq/consult/toggle',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ enabled: v }) });
      setConsMsg(v ? '✅ Консультації увімкнено (видно юзерам).' : '✅ Консультації вимкнено.');
    } catch (e) { setConsMsg('❌ ' + e.message); }
  };
  const editConsult = (cs) => { setConsForm(cs ? { ...cs } : { name: '', spec: '', bio: '', active: true, sort: 0 }); setConsPhoto(null); setConsMsg(''); setPkgRow(null); setEditPkgs([]); if (cs && cs.id) loadEditPkgs(cs.id); };
  const setCF = (k, v) => setConsForm(f => ({ ...f, [k]: v }));
  const saveConsult = async () => {
    if (!consForm || !(consForm.name || '').trim()) { setConsMsg('❌ Вкажи ім\'я астролога.'); return; }
    setConsBusy(true); setConsMsg('');
    try {
      const fd = new FormData();
      if (consForm.id) fd.append('id', String(consForm.id));
      fd.append('name', consForm.name || ''); fd.append('spec', consForm.spec || '');
      fd.append('price', consForm.price || ''); fd.append('bio', consForm.bio || '');
      fd.append('active', consForm.active ? '1' : '0'); fd.append('sort', String(consForm.sort || 0));
      fd.append('tg_username', consForm.tg_username || '');
      fd.append('tg_id', consForm.tg_id || '');
      fd.append('experience', consForm.experience || ''); fd.append('city', consForm.city || '');
      fd.append('country', consForm.country || ''); fd.append('age', consForm.age || '');
      fd.append('phone', consForm.phone || ''); fd.append('work_pref', consForm.work_pref || '');
      fd.append('payout_details', consForm.payout_details || ''); fd.append('languages', consForm.languages || '');
      if (consPhoto) fd.append('file', consPhoto);
      const r = await fetch('/api/dashboard/zodiaq/consultant', { method: 'POST', headers: AUTH(), body: fd });
      const d = await r.json();
      if (r.ok && d.ok) { setConsMsg('✅ Астролога збережено.'); setConsForm(null); setConsPhoto(null); await loadConsult(); }
      else setConsMsg('❌ Помилка збереження.');
    } catch (e) { setConsMsg('❌ ' + e.message); }
    setConsBusy(false);
  };
  const deleteConsult = async (id) => {
    if (!window.confirm('Видалити цього астролога?')) return;
    try {
      await fetch('/api/dashboard/zodiaq/consultant/delete', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ id }) });
      await loadConsult();
    } catch (e) { /* тихо */ }
  };
  const viewConsReceipt = async (rid) => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/consult/receipt/' + rid + '/img', { headers: AUTH() });
      if (!r.ok) { setConsMsg('❌ Чек недоступний.'); return; }
      setConsReceipt({ rid, url: URL.createObjectURL(await r.blob()) });
    } catch (e) { setConsMsg('❌ ' + e.message); }
  };
  const decideConsult = async (id, approve) => {
    setConsBusy(true); setConsMsg('');
    try {
      const url = '/api/dashboard/zodiaq/consult/' + (approve ? 'approve' : 'reject');
      const r = await fetch(url, { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ id, reason: '' }) });
      const d = await r.json();
      if (r.ok && d.ok) { setConsMsg(approve ? '✅ Підтверджено — заявку й натальні дані надіслано, юзер сповіщений.' : '✅ Заявку відхилено.'); await loadConsult(); }
      else setConsMsg('❌ Помилка.');
    } catch (e) { setConsMsg('❌ ' + e.message); }
    setConsBusy(false);
  };
  const deleteConsultReq = async (id) => {
    if (!window.confirm('Видалити цю заявку на консультацію (+ її чат)?')) return;
    try {
      await fetch('/api/dashboard/zodiaq/consult/request/delete', { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ id }) });
      setConsMsg('✅ Заявку видалено.'); await loadConsult();
    } catch (e) { setConsMsg('❌ ' + e.message); }
  };
  const consPending = consReqs.filter(r => r.status === 'pending').length;

  // ── ПІДТРИМКА (чат з автоперекладом) ──
  const [threads, setThreads] = useState([]);
  const [supTid, setSupTid] = useState(null);
  const [supMsgs, setSupMsgs] = useState([]);
  const [supReply, setSupReply] = useState('');
  const [supBusy, setSupBusy] = useState(false);
  const [supDeliver, setSupDeliver] = useState('');
  const loadThreads = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/support/threads', { headers: AUTH() });
      const d = await r.json();
      setThreads((d && d.threads) || []);
    } catch (e) { /* тихо */ }
  }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);
  const openThread = async (tid) => {
    setSupTid(tid); setSupMsgs([]); setSupReply(''); setSupDeliver('');
    try {
      const r = await fetch('/api/dashboard/zodiaq/support/thread/' + tid, { headers: AUTH() });
      const d = await r.json();
      setSupMsgs((d && d.messages) || []);
      loadThreads();
    } catch (e) { /* тихо */ }
  };
  const sendReply = async () => {
    const text = supReply.trim(); if (!text || !supTid) return;
    setSupBusy(true);
    try {
      const r = await fetch('/api/dashboard/zodiaq/support/reply',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ tid: String(supTid), text }) });
      const d = await r.json();
      if (r.ok && d.ok) {
        setSupReply('');
        setSupDeliver(d.delivered
          ? '✅ Доставлено юзеру в бот.'
          : '⚠️ Не доставлено в бот (юзер ще не тиснув Start). Відповідь збережено — юзер побачить її в підтримці всередині Mini App.');
        await openThread(supTid);
      }
    } catch (e) { /* тихо */ }
    setSupBusy(false);
  };
  const supUnread = threads.reduce((a, t) => a + (t.unread || 0), 0);

  const STATUS_CHIP = { pending: ['⏳ очікує', 'rgba(255,212,121,.18)', '#ffd479'],
    approved: ['✅ схвалено', 'rgba(57,229,140,.15)', '#39e58c'],
    rejected: ['✖ відхилено', 'rgba(255,107,107,.15)', '#ff6b6b'],
    scheduled: ['📅 призначено', 'rgba(34,211,238,.15)', '#22d3ee'],
    chatting: ['💬 чат', 'rgba(124,92,255,.2)', '#cbb8ff'] };

  // ── картка юзера + живий відлік преміуму ──
  const [userDetail, setUserDetail] = useState(null);
  const [grantMonths, setGrantMonths] = useState(1);
  const [grantMsg, setGrantMsg] = useState('');
  // 🎁 2026-09-11: подарунок токенів із картки юзера
  const [giftAmount, setGiftAmount] = useState('10');
  const [giftNotify, setGiftNotify] = useState(true);
  const [giftMsg, setGiftMsg] = useState('');
  const [giftBusy, setGiftBusy] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const anyPremium = users.some(u => u.premium);
  useEffect(() => {
    if (!userDetail && !anyPremium) return undefined;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [userDetail, anyPremium]);

  const giftCredits = async (tid) => {
    const n = Math.floor(Number(giftAmount) || 0);
    if (!(n > 0)) { setGiftMsg('❌ Вкажи, скільки токенів дарувати'); return; }
    setGiftBusy(true); setGiftMsg('…');
    try {
      // Хвилинна мітка у примітці НЕ для краси: ZODIAQ робить ключ ідемпотентності
      // з (tid + сума + примітка). Без неї другий такий самий подарунок тихо не
      // нарахувався б, а подвійний клік — навпаки, захищений (та сама хвилина).
      const stamp = new Date().toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
      const r = await fetch('/api/dashboard/zodiaq/credits/grant', {
        method: 'POST', headers: JSON_AUTH(),
        body: JSON.stringify({ tid: String(tid), amount: n, note: 'подарунок з дашборду ' + stamp, notify: giftNotify }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) setGiftMsg('❌ ' + (d.detail || d.error || 'не вдалося нарахувати'));
      else if (d.duplicate) setGiftMsg('⚠️ Таке саме нарахування вже було цієї хвилини. Баланс: ' + d.balance);
      else {
        let m = '✅ Подаровано ' + n + ' · баланс: ' + d.balance;
        if (!giftNotify) m += ' · без вітання';
        else if (d.notified) m += ' · вітання надіслано' + (d.notify_how === 'photo' ? ' 🖼' : ' (текстом)') + (d.notify_effect ? ' 🎉' : '');
        else m += ' · ⚠️ вітання НЕ дійшло: ' + (d.notify_error || 'невідома причина');
        setGiftMsg(m);
        setUserDetail(u => (u ? { ...u, credits: d.balance } : u));
        load();
      }
    } catch (e) { setGiftMsg('❌ ' + e.message); }
    setGiftBusy(false);
  };

  const grantPremium = async (tid, months) => {
    setGrantMsg('…');
    try {
      const r = await fetch('/api/dashboard/zodiaq/premium/grant',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ tid: String(tid), months }) });
      const d = await r.json();
      if (r.ok && d.ok) { setGrantMsg('✅ Преміум видано, юзер сповіщений.'); await load(); setUserDetail(null); }
      else setGrantMsg('❌ Помилка.');
    } catch (e) { setGrantMsg('❌ ' + e.message); }
  };
  const revokePremium = async (tid) => {
    setGrantMsg('…');
    try {
      const r = await fetch('/api/dashboard/zodiaq/premium/revoke',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ tid: String(tid) }) });
      const d = await r.json();
      if (r.ok && d.ok) { setGrantMsg('✅ Преміум знято.'); await load(); setUserDetail(null); }
      else setGrantMsg('❌ Помилка.');
    } catch (e) { setGrantMsg('❌ ' + e.message); }
  };
  const fmtCountdown = (iso) => {
    if (!iso) return '—';
    const ms = new Date(iso).getTime() - nowTs;
    if (ms <= 0) return 'завершився';
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${d}д ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };
  const fmtBirthday = (iso) => {
    if (!iso) return null;
    const [y, mo, da] = iso.split('-');
    return `${da}.${mo}.${y}`;
  };

  const paper = { p: 3, mb: 3, bgcolor: '#140f24', border: '1px solid rgba(150,130,255,.15)', borderRadius: 3 };
  const pendingCount = payments.filter(p => p.status === 'pending').length;

  // ── вкладки (див. ZQ_TABS вгорі) ──
  // Секції невидимої вкладки лишаються ЗМОНТОВАНИМИ (display:none), а не
  // розмонтованими: це зберігає їхній локальний стан і не перезапускає
  // завантаження даних при кожному перемиканні — тобто поведінка рівно та сама,
  // що й до вкладок, змінюється лише те, що видно на екрані.
  const [zqTab, setZqTab] = useState(0);
  const _zqVis = (id) => ((ZQ_TABS[zqTab] && ZQ_TABS[zqTab].ids.includes(id)) ? 'block' : 'none');

  // Лічильники для вкладок. Те саме джерело, що й у бічного меню, тож
  // числа не розійдуться: там воно каже «щось є», тут — де саме.
  const [zqTabCounts, setZqTabCounts] = useState({});
  useEffect(() => {
    const load = () => fetch('/api/dashboard/notifications-counts', { headers: AUTH() })
      .then((r) => r.json()).then((d) => setZqTabCounts(d || {})).catch(() => {});
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);

  // ── Токени (гаманець ZODIAQ) ──
  const [zqCr, setZqCr] = useState(null);
  const [zqCrVals, setZqCrVals] = useState({});
  const [zqCrMsg, setZqCrMsg] = useState('');
  const [zqCrBusy, setZqCrBusy] = useState(false);
  const [zqCrLedger, setZqCrLedger] = useState([]);
  const [zqCrFilter, setZqCrFilter] = useState('');
  const [zqGrantTid, setZqGrantTid] = useState('');
  const [zqGrantAmt, setZqGrantAmt] = useState('');
  const [zqGrantNote, setZqGrantNote] = useState('');
  const [zqGrantMsg, setZqGrantMsg] = useState('');
  const [zqAiVals, setZqAiVals] = useState({});
  const [zqEcon, setZqEcon] = useState(null);
  // Налаштування способів оплати. Окремо від цін: тут переважно тексти
  // й ключі, і мішати їх із числами в одній формі означало б плутати.
  const [zqPay, setZqPay] = useState({ requisites: {} });
  const [zqPayMsg, setZqPayMsg] = useState('');
  const [zqClaims, setZqClaims] = useState([]);
  const [zqClaimMsg, setZqClaimMsg] = useState('');
  const [zqJars, setZqJars] = useState([]);
  // ⚠️ Відповіді про гроші показуємо ВІКНОМ по центру, а не рядком унизу.
  // Дрібний текст того ж кольору, що й усе поруч, читається як частина
  // фону — і незрозуміло, спрацювало щось чи ні.
  const [zqDlg, setZqDlg] = useState(null);
  const tell = (text, ok) => setZqDlg({ text: String(text || ''), ok: !!ok });

  const loadClaims = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/topup/requests?status=pending', { headers: AUTH() });
      const d = await r.json();
      setZqClaims((d && d.rows) || []);
    } catch (e) { setZqClaims([]); }
  }, []);

  const decideClaim = async (id, approve) => {
    if (!approve && !window.confirm('Відхилити заявку №' + id + '?')) return;
    setZqClaimMsg('');
    try {
      const r = await fetch('/api/dashboard/zodiaq/topup/decide', {
        method: 'POST', headers: JSON_AUTH(),
        body: JSON.stringify({ id, approve, note: '' }),
      });
      const d = await r.json();
      if (d && d.already) tell('Заявку вже розглянуто раніше (' + d.status + ').', true);
      else if (d && d.ok) tell(approve
        ? ('Зараховано ' + d.credits + ' 💎.\nНовий баланс юзера: ' + d.balance)
        : 'Заявку відхилено. Юзеру надіслано повідомлення.', true);
      else tell('Не вдалось провести заявку', false);
      loadClaims(); loadCredits(); loadEcon();
    } catch (e) { tell('Не вдалось провести заявку', false); }
  };

  const monoPoll = async () => {
    setZqClaimMsg('Читаю виписку…');
    try {
      const r = await fetch('/api/dashboard/zodiaq/topup/mono-poll', { method: 'POST', headers: JSON_AUTH(), body: '{}' });
      const d = await r.json();
      setZqClaimMsg('');
      if (d && d.ok) {
        const err = (d.errors && d.errors.length) ? ('\n\n⚠️ ' + d.errors.join('; ')) : '';
        tell('Переглянуто надходжень: ' + d.checked
          + '\nЗараховано платежів: ' + d.credited
          + (d.credited ? '' : '\n\nЦе нормально, якщо ніхто ще не переказував на банку.')
          + err, !err);
      } else {
        tell('Не вдалось прочитати виписку.\n\nПричина: '
          + ((d && d.error) || '—')
          + (d && d.error === 'mono_token_not_set' ? '\nВстав токен і збережи.' : '')
          + (d && d.error === 'jar_id_not_set' ? '\nВстав id банки і збережи.' : ''), false);
      }
      loadCredits(); loadEcon();
    } catch (e) { setZqClaimMsg(''); tell('Не вдалось звернутись до ZODIAQ', false); }
  };

  // id банки в застосунку monobank не показується взагалі — його видно лише
  // через API. Тому питаємо його збереженим токеном і показуємо тут.
  const showJars = async () => {
    setZqPayMsg('Питаю monobank…');
    try {
      const r = await fetch('/api/dashboard/zodiaq/mono/jars', { headers: AUTH() });
      const d = await r.json();
      if (!d || !d.ok) {
        setZqPayMsg(d && d.error === 'mono_token_not_set'
          ? 'Спершу встав токен monobank і збережи'
          : ('monobank не відповів: ' + ((d && d.error) || '—')));
        return;
      }
      if (!(d.jars || []).length) { setZqPayMsg('Банок не знайдено — створи банку в застосунку'); return; }
      setZqPayMsg('');
      setZqJars(d.jars);
    } catch (e) { setZqPayMsg('Не вдалось'); }
  };

  // Пояснення пишуться самі при першому перегляді. Скидання потрібне тоді,
  // коли текст вийшов невдалим: інакше довелось би лізти в базу руками.
  //
  // ⚠️ Спершу тут було поле «впиши ключ». Так користуватись кнопкою не міг
  // ніхто: ключі на кшталт sign_Сонце треба було памʼятати, а стирати текст,
  // якого не бачиш, — це рішення наосліп. Тому список із повним текстом, а
  // кнопка скидання стоїть у рядку того пояснення, яке ти щойно прочитав.
  const [zqExpl, setZqExpl] = useState(null);
  const [zqExplList, setZqExplList] = useState([]);
  const [zqExplOpen, setZqExplOpen] = useState('');

  const loadExplStats = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/explains/stats', { headers: AUTH() });
      setZqExpl(await r.json());
    } catch (e) { setZqExpl(null); }
    try {
      const r2 = await fetch('/api/dashboard/zodiaq/explains/list?lang=uk&limit=300', { headers: AUTH() });
      const d2 = await r2.json();
      setZqExplList(d2 && Array.isArray(d2.items) ? d2.items : []);
    } catch (e) { setZqExplList([]); }
  }, []);

  const resetExpl = async (slot) => {
    const k = String(slot || '').trim();
    if (!k) return;
    if (!window.confirm('Прибрати пояснення «' + k + '» усіма мовами?\n\nВоно напишеться наново, щойно хтось відкриє цей пункт.')) return;
    try {
      const r = await fetch('/api/dashboard/zodiaq/explains/reset', {
        method: 'POST', headers: JSON_AUTH(), body: JSON.stringify({ key: k }),
      });
      const d = await r.json();
      if (d && d.ok) {
        tell('Прибрано записів: ' + d.removed + '\nСлот: ' + d.slot
          + '\n\nНове пояснення напишеться, щойно хтось відкриє цей пункт.', true);
        loadExplStats();
      } else tell('Не вдалось скинути', false);
    } catch (e) { tell('Не вдалось скинути', false); }
  };

  // Токен monobank уже налаштований для PlayLanguage. Перевипускати ключ,
  // яким користуються інші продукти, заради одного нового — зайвий ризик,
  // тож переносимо наявний. Значення при цьому ніде не показується.
  const importMonoToken = async () => {
    setZqPayMsg('Переношу токен із PlayLanguage…');
    try {
      const r = await fetch('/api/dashboard/zodiaq/mono/import-token', { method: 'POST', headers: JSON_AUTH(), body: '{}' });
      const d = await r.json();
      setZqPayMsg('');
      if (d && d.ok) { tell('Токен monobank перенесено в ZODIAQ.\n\nТепер натисни «Показати мої банки».', true); await loadCredits(); }
      else tell(d && d.error === 'no_token_in_playlanguage'
        ? 'У PlayLanguage токена немає — його треба вписати вручну.'
        : 'Не вдалось перенести токен.', false);
    } catch (e) { setZqPayMsg(''); tell('Не вдалось перенести токен', false); }
  };

  const savePay = async () => {
    setZqPayMsg('');
    const body = {
      topup_packs: String(zqPay.topup_packs ?? ''),
      topup_bonus_pct: String(zqPay.topup_bonus_pct ?? ''),
      stripe_min_eur: String(zqPay.stripe_min_eur ?? ''),
      fx_margin_percent: String(zqPay.fx_margin_percent ?? ''),
      mono_jar_url_uah: String(zqPay.mono_jar_url_uah ?? ''),
      mono_jar_id_uah: String(zqPay.mono_jar_id_uah ?? ''),
      requisites: zqPay.requisites || {},
    };
    // ⚠️ Токен шлемо ЛИШЕ якщо його щойно ввели. Порожнє поле не має
    // стирати ключ: випадкове збереження форми вимкнуло б автозарахування.
    if ((zqPay.mono_token || '').trim()) body.mono_token = zqPay.mono_token.trim();
    try {
      const w = await fetch('/api/dashboard/zodiaq/credits/config',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify(body) });
      const wd = await w.json();
      if (!wd || !wd.ok) { setZqPayMsg('Сервер не прийняв зміни'); return; }
      setZqPay((p) => ({ ...p, mono_token: '' }));
      await loadCredits();
      tell('Способи оплати збережено.', true);
    } catch (e) { tell('Не вдалось зберегти способи оплати', false); }
  };

  const loadEcon = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/credits/economics?limit=200', { headers: AUTH() });
      const d = await r.json();
      setZqEcon(d && d.rows ? d : { rows: [] });
    } catch (e) { setZqEcon({ rows: [] }); }
  }, []);

  const loadCredits = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/zodiaq/credits/config', { headers: AUTH() });
      const d = await r.json();
      setZqCr(d);
      const v = { ...(d.prices || {}) };
      // ⚠️ Дні преміуму приходять ОКРЕМИМ полем: у базі вони лежать під власним
      // ключем, а не серед price_*. Підмішуємо, щоб форма була однорідна.
      v.ref_bonus_days = (typeof d.ref_bonus_days === 'number') ? d.ref_bonus_days : 7;
      setZqCrVals(v);
      setZqAiVals({ ...(d.ai_costs || {}) });
      // mono_token сюди НЕ приходить — сервер віддає лише mono_token_set.
      // Значення секрету в браузері не потрібне нікому, а витекти може.
      setZqPay({
        topup_packs: d.topup_packs || '', topup_bonus_pct: d.topup_bonus_pct,
        stripe_min_eur: d.stripe_min_eur, fx_margin_percent: d.fx_margin_percent,
        mono_jar_url_uah: d.mono_jar_url_uah || '', mono_jar_id_uah: d.mono_jar_id_uah || '',
        mono_token_set: !!d.mono_token_set, mono_token: '',
        rate: d.rate, rate_live: d.rate_live, requisites: { ...(d.requisites || {}) },
      });
    } catch (e) { setZqCrMsg('Не вдалось прочитати налаштування'); }
  }, []);

  const loadCrLedger = useCallback(async (tid) => {
    try {
      const q = '/api/dashboard/zodiaq/credits/ledger?limit=200' + (tid ? ('&tid=' + encodeURIComponent(tid)) : '');
      const r = await fetch(q, { headers: AUTH() });
      const d = await r.json();
      setZqCrLedger(d.rows || []);
    } catch (e) { setZqCrLedger([]); }
  }, []);

  useEffect(() => { loadCredits(); loadCrLedger(''); loadEcon(); loadClaims(); loadExplStats(); },
    [loadCredits, loadCrLedger, loadEcon, loadClaims, loadExplStats]);

  // 🔮 2026-09-11: модель кожного рівня. Бекенд перевіряє її ЖИВИМ викликом до
  // OpenAI і відмовляє, якщо модель недоступна на ключі — інакше тлумачення
  // почали б падати вже у юзера, а дашборд показував би «збережено».
  const saveTierModel = async (tier, model) => {
    try {
      const body = {}; body['tier' + tier + '_model'] = model;
      const r = await fetch('/api/dashboard/zodiaq/credits/config',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify(body) });
      const d = await r.json();
      const got = String(((d || {}).saved || {})['tier' + tier + '_model'] || '');
      if (!got) tell('Сервер не прийняв модель', false);
      else if (got.indexOf('відхилено') === 0) tell(got, false);
      else { tell('Модель рівня оновлено: ' + got, true); loadCredits(); }
    } catch (e) { tell('Не вдалось зберегти модель', false); }
  };

  const saveCredits = async () => {
    const body = {}; const bad = [];
    ZQ_CREDIT_FIELDS.forEach(([k, label]) => {
      const raw = String(zqCrVals[k] ?? '').trim();
      // ⚠️ Number('') дорівнює 0, а не NaN. Порожнє поле мовчки поставило б
      // нуль — тобто «пункт безкоштовно» або «нуль токенів за євро».
      if (raw === '') { bad.push(label + ': порожнє'); return; }
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0) { bad.push(label + ': не ціле число'); return; }
      body[k] = n;
    });
    // Вартість AI — ДРОБОВА, тож перевіряється окремо: Number.isInteger тут
    // відкинув би цілком нормальні 0.0004.
    ZQ_AI_FIELDS.forEach(([k, label]) => {
      const raw = String(zqAiVals[k] ?? '').trim();
      if (raw === '') { bad.push(label + ': порожнє'); return; }
      const n = Number(raw);
      if (!isFinite(n) || n < 0) { bad.push(label + ': не число'); return; }
      body[k] = n;
    });
    if (bad.length) { setZqCrMsg('Не збережено — ' + bad.join('; ')); return; }
    setZqCrBusy(true); setZqCrMsg('');
    try {
      const w = await fetch('/api/dashboard/zodiaq/credits/config',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify(body) });
      const wd = await w.json();
      if (!wd || !wd.ok) { setZqCrMsg('Сервер не прийняв зміни'); setZqCrBusy(false); return; }
      // Не віримо власному запиту: перечитуємо і показуємо те, що сервер
      // СПРАВДІ зберіг. Мовчазна відмова тут коштувала б грошей.
      const r = await fetch('/api/dashboard/zodiaq/credits/config', { headers: AUTH() });
      const d = await r.json();
      setZqCr(d);
      const now = { ...(d.prices || {}) };
      now.ref_bonus_days = (typeof d.ref_bonus_days === 'number') ? d.ref_bonus_days : 7;
      setZqCrVals(now);
      setZqAiVals({ ...(d.ai_costs || {}) });
      loadEcon();          // ціни змінились — перерахунок прибутку теж
      const back = { ...now, ...(d.ai_costs || {}) };
      const diff = [...ZQ_CREDIT_FIELDS, ...ZQ_AI_FIELDS]
        .filter(([k]) => Number(back[k]) !== Number(body[k]));
      setZqCrMsg(diff.length
        ? ('⚠️ На сервері інші значення: ' + diff.map(([k, l]) => l + ' = ' + back[k]).join(', '))
        : '✅ Збережено і перевірено на сервері');
    } catch (e) { setZqCrMsg('Не вдалось зберегти'); }
    setZqCrBusy(false);
  };

  const grantCredits = async () => {
    const tid = zqGrantTid.trim(); const raw = zqGrantAmt.trim();
    if (!tid) { setZqGrantMsg('Вкажи Telegram ID'); return; }
    if (raw === '') { setZqGrantMsg('Вкажи кількість'); return; }
    const amt = Number(raw);
    if (!Number.isInteger(amt) || amt === 0) { setZqGrantMsg('Кількість — ціле число, не нуль'); return; }
    // велика сума найчастіше означає зайвий нуль
    if (Math.abs(amt) > 100 && !window.confirm('Провести ' + amt + ' токенів юзеру ' + tid + '?')) return;
    setZqCrBusy(true); setZqGrantMsg('');
    try {
      const r = await fetch('/api/dashboard/zodiaq/credits/grant', {
        method: 'POST', headers: JSON_AUTH(),
        body: JSON.stringify({ tid, amount: amt, note: zqGrantNote.trim() }),
      });
      const d = await r.json();
      if (d && d.error === 'insufficient_credits') setZqGrantMsg('Не проведено: у юзера лише ' + d.balance + ' токенів');
      else if (d && d.error === 'no_user') setZqGrantMsg('Такого юзера немає');
      else if (d && d.duplicate) setZqGrantMsg('Така сама операція вже проводилась — повторно не нараховано. Зміни помітку, якщо це справді нова операція. Баланс: ' + d.balance);
      else if (d && d.ok) { setZqGrantMsg('✅ Проведено. Новий баланс: ' + d.balance); setZqGrantAmt(''); loadCredits(); loadCrLedger(zqCrFilter.trim()); }
      else setZqGrantMsg('Не вдалось провести');
    } catch (e) { setZqGrantMsg('Не вдалось провести'); }
    setZqCrBusy(false);
  };

  // ── перетягування секцій ZODIAQ (порядок зберігається у localStorage) ──
  const _zqSecIds = ['s0', 's17', 's1', 's2', 's3', 's4', 's16', 's5', 's6', 's7', 's8', 's9', 's10', 's11', 's12', 's13', 's14', 's15'];
  const [_zqOrder, _setZqOrder] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem('zq_sec_order') || 'null'); if (Array.isArray(v) && v.length) return v; } catch (e) {}
    return _zqSecIds;
  });
  const _zqOrd = [..._zqOrder.filter(x => _zqSecIds.includes(x)), ..._zqSecIds.filter(x => !_zqOrder.includes(x))];
  const _zqMove = (to) => {
    const from = window._zqDrag; window._zqDrag = null;
    if (!from || from === to) return;
    const arr = _zqOrd.filter(x => x !== from); arr.splice(arr.indexOf(to), 0, from);
    _setZqOrder(arr); try { localStorage.setItem('zq_sec_order', JSON.stringify(arr)); } catch (e) {}
  };
  const _zqReset = () => { _setZqOrder(_zqSecIds); try { localStorage.removeItem('zq_sec_order'); } catch (e) {} };
  const _zqDP = (id) => ({ 'data-zqsec': id, onDragOver: (e) => e.preventDefault(), onDrop: () => _zqMove(id) });
  // тач: HTML5 DnD не працює на телефоні/планшеті → знаходимо секцію під пальцем через elementFromPoint
  const _zqTouchMove = (e) => {
    if (!window._zqTouching) return;
    const t = e.touches && e.touches[0]; if (!t) return;
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const sec = (el && el.closest) ? el.closest('[data-zqsec]') : null;
    if (window._zqLastOverEl && window._zqLastOverEl !== sec) window._zqLastOverEl.style.outline = '';
    if (sec) { sec.style.outline = '2px dashed #22d3ee'; sec.style.outlineOffset = '2px'; window._zqLastOverEl = sec; window._zqOver = sec.getAttribute('data-zqsec'); }
    else { window._zqOver = null; }
  };
  const _zqTouchEnd = () => {
    window._zqTouching = false;
    if (window._zqLastOverEl) { window._zqLastOverEl.style.outline = ''; window._zqLastOverEl = null; }
    const to = window._zqOver; window._zqOver = null;
    if (to) _zqMove(to);
  };
  const _zqGrip = (id) => (<Box
    draggable
    onDragStart={(e) => { window._zqDrag = id; if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; }}
    onTouchStart={() => { window._zqDrag = id; window._zqTouching = true; }}
    onTouchMove={_zqTouchMove}
    onTouchEnd={_zqTouchEnd}
    sx={{ position: 'absolute', top: 0, right: 0, px: 1.2, py: 0.7, cursor: 'grab', color: '#8a83b0', fontSize: 24, lineHeight: 1, userSelect: 'none', WebkitUserSelect: 'none', zIndex: 3, touchAction: 'none' }}
    title="Перетягни, щоб змінити порядок">⠿</Box>);

  return (
    <Container maxWidth="lg" sx={{ py: 3, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" sx={{ order: -3, fontWeight: 800, mb: .5,
        background: 'linear-gradient(120deg,#ffd479,#22d3ee)', WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent' }}>
        🔮 ZODIAQ
      </Typography>
      <Typography sx={{ order: -3, color: '#9a92c4', mb: 1 }}>Гороскоп-міні-апп · керування</Typography>
      <Box sx={{ order: -2, display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ color: '#6a6490', fontSize: 12 }}>↕ Перетягни секції за ⠿, щоб змінити порядок у межах вкладки</Typography>
        <Button size="small" onClick={_zqReset} sx={{ color: '#22d3ee', fontSize: 11 }}>↺ Скинути порядок</Button>
      </Box>

      {/* order:-1 — контейнер сторінки це flex-колонка, де секції розставляються
          через CSS order; вкладки мусять сидіти між шапкою (-3/-2) і секціями (0..13).
          scrollable — щоб 7 вкладок не стискались у кашу на вузькому екрані. */}
      <Tabs value={zqTab} onChange={(_, v) => setZqTab(v)} variant="scrollable" scrollButtons="auto"
        allowScrollButtonsMobile
        TabIndicatorProps={{ style: { backgroundColor: 'var(--ac1, #7c4dff)' } }}
        sx={{ order: -1, mb: 2, minHeight: 42, borderBottom: '1px solid rgba(150,130,255,.15)',
          '& .MuiTab-root': { color: '#8a83b0', minHeight: 42, textTransform: 'none', fontSize: 14 },
          '& .Mui-selected': { color: '#c9b8ff' } }}>
        {ZQ_TABS.map((t) => {
          const n = t.badge ? (zqTabCounts[t.badge] || 0) : (t.newUsers ? newCount : 0);
          return <Tab key={t.label} label={n > 0 ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: .8 }}>
              {t.label}
              <Box component="span" sx={{ bgcolor: '#d32f2f', color: '#fff', borderRadius: '10px',
                px: .8, fontSize: 11, fontWeight: 700, lineHeight: '18px', minWidth: 18, textAlign: 'center' }}>
                {n}
              </Box>
            </Box>
          ) : t.label} />;
        })}
      </Tabs>
      <AppQuickNav order={-1} />

      {/* Відповіді про гроші — вікном по центру. Рядок унизу дрібним шрифтом
          того ж кольору, що й сусідній текст, читається як частина фону. */}
      <Dialog open={!!zqDlg} onClose={() => setZqDlg(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1730', border: '1px solid ' + (zqDlg && zqDlg.ok ? 'rgba(127,227,176,.45)' : 'rgba(255,107,107,.45)'), borderRadius: 3 } }}>
        <DialogTitle sx={{ color: zqDlg && zqDlg.ok ? '#7fe3b0' : '#ff6b6b', fontWeight: 800, fontSize: 18 }}>
          {zqDlg && zqDlg.ok ? '✅ Готово' : '⚠️ Не вийшло'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#eee9ff', fontSize: 15, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
            {zqDlg ? zqDlg.text : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="warning" onClick={() => setZqDlg(null)}>Зрозуміло</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ 🔑 СЕКРЕТ АДМІНКИ ZODIAQ ═══ */}
      <Paper sx={{ ...paper, display: _zqVis('secret') }}>
        <Typography variant="h6" sx={{ color: '#eee9ff', mb: 1 }}>🔑 Секрет адмінки Zodiaq</Typography>
        <Typography sx={{ color: '#9a92c4', fontSize: 13, mb: 1.5 }}>
          Пароль до окремої сторінки admin.html Zodiaq (не токен бота). Встав тут — дашборд одразу почне ним користуватись.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
          <Typography sx={{ color: '#6a6490', fontSize: 12 }}>Поточний: {zqMasked() || '— не збережено —'}</Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <TextField size="small" placeholder="Встав новий токен" value={zqTokenInput}
            onChange={e => setZqTokenInput(e.target.value)} sx={{ minWidth: 260 }} />
          <Button variant="contained" onClick={saveZqToken} sx={{ bgcolor: '#22d3ee', color: '#07070f' }}>Зберегти</Button>
          <Button variant="outlined" onClick={openZqAdmin} sx={{ borderColor: '#7c5cff', color: '#7c5cff' }}>🔗 Відкрити admin.html</Button>
        </Stack>
        {zqMsg && <Typography sx={{ mt: 1, fontSize: 13, color: zqMsg.startsWith('✅') ? '#4ade80' : '#ff6b6b' }}>{zqMsg}</Typography>}
      </Paper>

      {/* ═══ КРОС-ПРОМО: ЗАПРОСИТИ В ZODIAQ ═══ */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s0'), position: 'relative', display: _zqVis('s0') }} {..._zqDP('s0')}>{_zqGrip('s0')}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ color: '#eee9ff' }}>📣 Запросити в ZODIAQ</Typography>
          <Button size="small" onClick={loadInvCounts} sx={{ color: '#22d3ee' }}>↻ Оновити</Button>
        </Stack>
        <Typography sx={{ color: '#9a92c4', fontSize: 13, mb: 1.5 }}>
          Розсилка вітання ZODIAQ (лого + текст мовою юзера + кнопка «🔮 Ввімкнути ZODIAQ») юзерам іншого продукту через його бот.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
          {[['pl', 'PL · @PlayLanguage_bot'], ['media', 'Media · @media_2026_bot']].map(([a, lbl]) => {
            const cnt = invCounts && invCounts[a] ? invCounts[a].count : null;
            return (
              <Chip key={a} clickable onClick={() => pickAudience(a)}
                label={lbl + (cnt != null ? (' · ' + cnt) : '')}
                sx={{ bgcolor: invAud === a ? 'rgba(255,212,121,.25)' : 'rgba(124,92,255,.15)',
                  color: invAud === a ? '#ffd479' : '#cbb8ff', fontWeight: invAud === a ? 700 : 400,
                  border: invAud === a ? '1px solid rgba(255,212,121,.5)' : '1px solid transparent' }} />
            );
          })}
        </Stack>
        {/* Вибір конкретних учасників галочками */}
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: .5 }}>
            <Typography sx={{ color: '#c9c2e8', fontSize: 13, fontWeight: 700 }}>
              Кому надіслати · обрано {invSel.size}/{invUsers.length}
            </Typography>
            <Button size="small" onClick={invAll} sx={{ color: '#39e58c', minWidth: 0 }}>усіх</Button>
            <Button size="small" onClick={invNone} sx={{ color: '#ff9a9a', minWidth: 0 }}>нікого</Button>
          </Stack>
          <Box sx={{ maxHeight: 240, overflowY: 'auto', borderRadius: 2,
            border: '1px solid rgba(150,130,255,.18)', bgcolor: 'rgba(20,16,40,.5)', p: .5 }}>
            {invListBusy ? <Typography sx={{ color: '#9a92c4', fontSize: 13, p: 1 }}>Завантаження…</Typography>
              : invUsers.length === 0 ? <Typography sx={{ color: '#9a92c4', fontSize: 13, p: 1 }}>Немає отримувачів.</Typography>
              : invUsers.map(u => (
                <Stack key={u.tid} direction="row" alignItems="center" spacing={1}
                  onClick={() => toggleInv(u.tid)}
                  sx={{ px: 1, py: .5, borderRadius: 1, cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(124,92,255,.1)' } }}>
                  <input type="checkbox" readOnly checked={invSel.has(u.tid)} style={{ accentColor: '#ffd479' }} />
                  <Typography sx={{ color: '#eee9ff', fontSize: 13 }}>{u.name}</Typography>
                  <Typography sx={{ color: '#8a83b4', fontSize: 11 }}>{u.lang} · {u.tid}</Typography>
                </Stack>
              ))}
          </Box>
        </Box>
        <Typography sx={{ color: '#8a83b4', fontSize: 11, mb: 1.5 }}>
          ℹ️ Avatar тут недоступний (окремий акаунт/бот). Кнопка веде на t.me/ZODI_AQ_bot?start=&lt;продукт&gt; — після Start юзер стає підписником ZODIAQ.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button variant="contained" onClick={sendInvite} disabled={invBusy || invSel.size === 0}
            sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>
            {invBusy ? 'Розсилаю…' : ('📤 Розіслати обраним (' + invSel.size + ')')}
          </Button>
          {invResult && <Typography sx={{ color: invResult.startsWith('✅') ? '#39e58c' : '#ffb454', fontSize: 13 }}>{invResult}</Typography>}
        </Stack>
      </Paper>

      {/* ═══ АНАЛІТИКА ═══ */}
      {stats && (
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s1'), position: 'relative', display: _zqVis('s1') }} {..._zqDP('s1')}>{_zqGrip('s1')}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#eee9ff' }}>📊 Аналітика</Typography>
          <Button size="small" onClick={loadStats} sx={{ color: '#22d3ee' }}>↻ Оновити</Button>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 1.5, mb: 2 }}>
          {[
            ['👥 Усього', stats.users.total, '#eee9ff'],
            ['🆕 Сьогодні', stats.users.today, '#39e58c'],
            ['📅 7 днів', stats.users.d7, '#c9c2e8'],
            ['📆 30 днів', stats.users.d30, '#c9c2e8'],
            ['💎 Преміум', stats.premium_active, '#ffd479'],
            ['🌌 З наталом', stats.users.with_natal, '#cbb8ff'],
          ].map(([k, v, col]) => (
            <Box key={k} sx={{ bgcolor: 'rgba(28,24,52,.5)', border: '1px solid rgba(150,130,255,.12)', borderRadius: 2, p: 1.5, textAlign: 'center' }}>
              <Typography sx={{ color: col, fontSize: 26, fontWeight: 800 }}>{v}</Typography>
              <Typography sx={{ color: '#9a92c4', fontSize: 12 }}>{k}</Typography>
            </Box>
          ))}
        </Box>
        {/* 14-денний приріст */}
        <Typography sx={{ color: '#c9c2e8', fontSize: 13, mb: 1 }}>Нові юзери за 14 днів:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.6, height: 70, mb: 2 }}>
          {(() => { const mx = Math.max(1, ...stats.signups14.map(s => s.count)); return stats.signups14.map((s, i) => (
            <Box key={i} title={s.date + ': ' + s.count} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
              <Box sx={{ width: '100%', height: (s.count / mx * 100) + '%', minHeight: s.count ? 4 : 1,
                background: s.count ? 'linear-gradient(180deg,#ffd479,#22d3ee)' : 'rgba(150,130,255,.15)', borderRadius: '4px 4px 0 0' }} />
              <Typography sx={{ color: '#6a6390', fontSize: 8, mt: 0.3 }}>{s.date.slice(8)}</Typography>
            </Box>
          )); })()}
        </Box>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Chip label={'🧾 Платежі: ' + stats.payments.approved + ' ✅ / ' + stats.payments.pending + ' ⏳ / ' + stats.payments.rejected + ' ✖'} size="small" sx={{ bgcolor: 'rgba(124,92,255,.15)', color: '#cbb8ff' }} />
          {stats.revenue.length > 0 && <Chip label={'💰 Дохід: ' + stats.revenue.map(r => r.sum + ' ' + r.currency).join(', ')} size="small" sx={{ bgcolor: 'rgba(57,229,140,.15)', color: '#39e58c' }} />}
          <Chip label={'🆘 Підтримка: ' + stats.support.threads + ' (нових ' + stats.support.unread + ')'} size="small" sx={{ bgcolor: 'rgba(255,212,121,.12)', color: '#ffd479' }} />
        </Stack>
      </Paper>
      )}

      {/* ═══ ПОДІЇ: поділитися ботом / донат ═══ */}
      {events && (
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s2'), position: 'relative', display: _zqVis('s2') }} {..._zqDP('s2')}>{_zqGrip('s2')}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ color: '#eee9ff' }}>🔔 Активність</Typography>
          <Button size="small" onClick={loadEvents} sx={{ color: '#22d3ee' }}>↻ Оновити</Button>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip label={'📤 Поділились: ' + ((events.summary.share || {}).total || 0) + ' (' + ((events.summary.share || {}).users || 0) + ' юзерів)'} size="small" sx={{ bgcolor: 'rgba(34,211,238,.15)', color: '#22d3ee' }} />
          <Chip label={'💛 Донат-кліки: ' + ((events.summary.donate || {}).total || 0) + ' (' + ((events.summary.donate || {}).users || 0) + ' юзерів)'} size="small" sx={{ bgcolor: 'rgba(255,212,121,.15)', color: '#ffd479' }} />
        </Stack>
        {events.events.length === 0
          ? <Typography sx={{ color: '#9a92c4', fontSize: 14 }}>Ще немає подій.</Typography>
          : (
            <Stack spacing={0.7}>
              {events.events.slice(0, 40).map((e, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="center" sx={{ fontSize: 13 }}>
                  <span>{e.kind === 'share' ? '📤' : '💛'}</span>
                  <Typography sx={{ color: '#eee9ff', flex: 1, fontSize: 13 }}>
                    {e.first_name || '—'}{e.username ? ' @' + e.username : ''} <span style={{ color: '#6a6390' }}>· {e.telegram_id}</span>
                  </Typography>
                  <Typography sx={{ color: '#9a92c4', fontSize: 12 }}>{e.kind === 'share' ? 'поділився' : 'донат'}</Typography>
                  <Typography sx={{ color: '#6a6390', fontSize: 11 }}>{e.at ? new Date(e.at).toLocaleString('uk-UA') : ''}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
      </Paper>
      )}

      {/* Завантаження лого */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s3'), position: 'relative', display: _zqVis('s3') }} {..._zqDP('s3')}>{_zqGrip('s3')}
        <Typography variant="h6" sx={{ color: '#eee9ff', mb: 1 }}>🎯 Лого в центрі кулі</Typography>
        <Typography sx={{ color: '#9a92c4', mb: 2, fontSize: 14 }}>
          Завантаж PNG/SVG (краще з прозорим фоном) — воно стане в центр скляного глобуса.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="contained" component="label" disabled={uploading}
            sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>
            {uploading ? 'Завантаження…' : 'Обрати файл'}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} />
          </Button>
          {logoMsg && <Typography sx={{ color: logoMsg.startsWith('✅') ? '#39e58c' : '#ff6b6b', fontSize: 14 }}>{logoMsg}</Typography>}
        </Stack>
      </Paper>

      {/* Вітальне фото */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s4'), position: 'relative', display: _zqVis('s4') }} {..._zqDP('s4')}>{_zqGrip('s4')}
        <Typography variant="h6" sx={{ color: '#eee9ff', mb: 1 }}>🖼 Вітальне фото (при /start)</Typography>
        <Typography sx={{ color: '#9a92c4', mb: 2, fontSize: 14 }}>
          Завантаж своє зображення — воно з'явиться у вітальному повідомленні замість згенерованого. Рекомендація: горизонтальне/квадратне, до 1280px.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button variant="contained" component="label" disabled={welUploading}
            sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>
            {welUploading ? 'Завантаження…' : 'Обрати файл'}
            <input ref={welRef} type="file" accept="image/*" hidden onChange={uploadWelcome} />
          </Button>
          <ZqAuthImg src={'/api/dashboard/zodiaq/img/welcome-image?v=' + (welMsg || '').length} style={{ height: 60, borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(150,130,255,.25)' }} />
          {welMsg && <Typography sx={{ color: welMsg.startsWith('✅') ? '#39e58c' : '#ff6b6b', fontSize: 14 }}>{welMsg}</Typography>}
        </Stack>
      </Paper>

      {/* 👥 Хто кого привів (05.10.2026) */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s17'), position: 'relative', display: _zqVis('s17') }} {..._zqDP('s17')}>{_zqGrip('s17')}
        <Typography variant="h6" sx={{ color: '#eee9ff', mb: .5 }}>👥 Реферали — хто кого привів</Typography>
        <Typography sx={{ color: '#9a92c4', fontSize: 13 }}>
          Скільки токенів за друзів — у вкладці «Монетизація» → «💎 Токени (гаманець)»: «За приведеного друга» і «Новачку за запрошенням».
        </Typography>
        <ZqRefSection />
      </Paper>

      {/* 💌 Запрошення «Поділитися ботом» */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s16'), position: 'relative', display: _zqVis('s16') }} {..._zqDP('s16')}>{_zqGrip('s16')}
        <Typography variant="h6" sx={{ color: '#eee9ff', mb: 1 }}>💌 Запрошення «Поділитися ботом»</Typography>
        <Typography sx={{ color: '#9a92c4', mb: 2, fontSize: 14 }}>
          Так другові приходить запрошення, коли людина в застосунку тисне «Поділитися» → «Надіслати запрошення».
          Текст і кнопка — окремо для кожної мови (мова того, хто ділиться). Кнопка завжди веде в бота з
          реферальним кодом того, хто запросив, — це не змінюється.
        </Typography>
        {!inv ? (
          <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} />
            <Typography sx={{ color: '#9a92c4', fontSize: 14 }}>{invMsg || 'Завантаження…'}</Typography></Stack>
        ) : (<>
          <Stack direction="row" spacing={.8} sx={{ mb: 2, flexWrap: 'wrap', rowGap: .8 }}>
            {INV_LANGS.map(([l, name]) => {
              const changed = (invEdit.caption[l] || '') !== (inv.caption[l] || '') || (invEdit.btn[l] || '') !== (inv.btn[l] || '');
              const custom = inv.custom_caption[l] || inv.custom_btn[l];
              const ownImg = ((inv.images || {})[l] || {}).source === 'lang';
              return <Chip key={l} label={name + (ownImg ? ' 🖼' : '') + (changed ? ' •' : '')} onClick={() => setInvLang(l)}
                sx={{ bgcolor: invLang === l ? 'rgba(255,212,121,.18)' : 'rgba(124,92,255,.12)',
                  color: invLang === l ? '#ffd479' : (custom ? '#39e58c' : '#cbb8ff'),
                  border: invLang === l ? '1px solid #ffd479' : '1px solid transparent', fontWeight: 600 }} />;
            })}
          </Stack>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Перегляд — як повідомлення в Telegram */}
            <Box sx={{ width: 320, maxWidth: '100%', flexShrink: 0 }}>
              <Typography sx={{ color: '#6a6490', fontSize: 12, mb: .8 }}>Так побачить друг у Telegram:</Typography>
              <Box sx={{ bgcolor: '#182533', borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(255,255,255,.06)' }}>
                <ZqAuthImg src={'/api/dashboard/zodiaq/img/share-invite?lang=' + invLang + '&t=' + invImgT}
                  style={{ width: '100%', display: 'block' }}
                  fallback={<Box sx={{ p: 3, textAlign: 'center', color: '#8a83b0', fontSize: 13 }}>
                    🎨 Картка малюється в застосунку мовою людини — зразок з'явиться після першого запрошення цією мовою.
                  </Box>} />
                <Box sx={{ px: 1.5, py: 1.2, color: '#f5f5f5', fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {invEdit.caption[invLang] || inv.default_caption[invLang]}
                </Box>
              </Box>
              <Box sx={{ mt: .6, bgcolor: 'rgba(24,37,51,.85)', borderRadius: '10px', py: 1, textAlign: 'center',
                color: '#fff', fontSize: 14, fontWeight: 600, border: '1px solid rgba(255,255,255,.06)' }}>
                {invEdit.btn[invLang] || inv.default_btn[invLang]} ↗
              </Box>
            </Box>

            {/* Редагування */}
            <Box sx={{ flex: 1, minWidth: 280 }}>
              {(() => {
                const invImgSrc = ((inv.images || {})[invLang] || {});
                const ln = invScopeName(invLang);
                return (<>
                  <Typography sx={{ color: '#c9c2e8', fontSize: 14, fontWeight: 600, mb: 1 }}>🖼 Картинка для {ln}</Typography>
                  <Typography sx={{ color: '#9a92c4', fontSize: 13, mb: 1 }}>
                    {invImgSrc.source === 'lang'
                      ? 'Зараз — своя картинка саме для ' + ln + (invImgSrc.ts ? ' (завантажено ' + invImgSrc.ts + ')' : '') + '.'
                      : invImgSrc.source === 'all'
                        ? 'Зараз — спільна картинка для всіх мов. Можеш дати ' + ln + ' свою.'
                        : 'Зараз — картка ZODIAQ, яку застосунок малює мовою людини (коло знаків, назва, 4 пункти).'}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                    <Button variant="contained" component="label" disabled={shInvBusy}
                      sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>
                      {invImgSrc.source === 'lang' ? 'Замінити для ' + ln : 'Завантажити для ' + ln}
                      <input type="file" accept="image/*" hidden onChange={(e) => uploadInvImg(e, invLang)} />
                    </Button>
                    {invImgSrc.source === 'lang' && (
                      <Button variant="outlined" disabled={shInvBusy} onClick={() => removeInvImg(invLang)}
                        sx={{ color: '#cbb8ff', borderColor: 'rgba(150,130,255,.4)' }}>↺ Прибрати картинку {ln}</Button>
                    )}
                  </Stack>
                </>);
              })()}
              <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(124,92,255,.07)', border: '1px solid rgba(150,130,255,.15)' }}>
                <Typography sx={{ color: '#c9c2e8', fontSize: 13, fontWeight: 600, mb: .5 }}>🌐 Спільна картинка для всіх мов</Typography>
                <Typography sx={{ color: '#9a92c4', fontSize: 12.5, mb: 1 }}>
                  {inv.has_image ? 'Є' + (inv.image_ts ? ' (завантажено ' + inv.image_ts + ')' : '') + ' — її отримують мови без своєї картинки.'
                    : 'Немає — мови без своєї картинки отримують намальовану картку.'}
                  {' '}Порядок: своя картинка мови → спільна → намальована. Найкраще — 4:5 (1080×1350) або квадрат.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <Button size="small" variant="outlined" component="label" disabled={shInvBusy}
                    sx={{ color: '#ffd479', borderColor: 'rgba(255,212,121,.45)' }}>
                    {inv.has_image ? 'Замінити спільну' : 'Завантажити спільну'}
                    <input type="file" accept="image/*" hidden onChange={(e) => uploadInvImg(e, 'all')} />
                  </Button>
                  {inv.has_image && (
                    <Button size="small" disabled={shInvBusy} onClick={() => removeInvImg('all')}
                      sx={{ color: '#cbb8ff' }}>↺ Прибрати спільну</Button>
                  )}
                </Stack>
              </Box>

              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography sx={{ color: '#c9c2e8', fontSize: 14, fontWeight: 600 }}>
                  ✍️ Текст ({(INV_LANGS.find(([l]) => l === invLang) || [])[1]})
                </Typography>
                <Chip size="small" label={inv.custom_caption[invLang] || inv.custom_btn[invLang] ? 'змінений' : 'стандартний'}
                  sx={{ bgcolor: inv.custom_caption[invLang] || inv.custom_btn[invLang] ? 'rgba(57,229,140,.15)' : 'rgba(124,92,255,.15)',
                    color: inv.custom_caption[invLang] || inv.custom_btn[invLang] ? '#39e58c' : '#cbb8ff' }} />
              </Stack>
              <TextField label="Текст під картинкою" size="small" fullWidth multiline minRows={5}
                value={invEdit.caption[invLang] || ''} onChange={(e) => setInvField('caption', e.target.value)}
                InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ maxLength: inv.caption_max, style: { color: '#eee9ff' } }}
                FormHelperTextProps={{ sx: { color: '#6a6490' } }}
                helperText={(invEdit.caption[invLang] || '').length + ' / ' + inv.caption_max + ' символів · емодзі можна'}
                sx={{ mb: 1.5 }} />
              <TextField label="Напис на кнопці" size="small" fullWidth
                value={invEdit.btn[invLang] || ''} onChange={(e) => setInvField('btn', e.target.value)}
                InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ maxLength: inv.btn_max, style: { color: '#eee9ff' } }}
                FormHelperTextProps={{ sx: { color: '#6a6490' } }}
                helperText={(invEdit.btn[invLang] || '').length + ' / ' + inv.btn_max} sx={{ mb: 1.5 }} />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Button variant="contained" disabled={shInvBusy || !invDirty} onClick={saveInv}
                  sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>
                  {shInvBusy ? 'Зберігаю…' : '💾 Зберегти всі мови'}
                </Button>
                <Button size="small" disabled={shInvBusy}
                  onClick={() => setInvEdit((p) => ({ caption: { ...p.caption, [invLang]: inv.default_caption[invLang] },
                    btn: { ...p.btn, [invLang]: inv.default_btn[invLang] } }))}
                  sx={{ color: '#22d3ee' }}>↺ Стандартний текст для цієї мови</Button>
                {invDirty && <Typography sx={{ color: '#ffd479', fontSize: 13 }}>• є незбережені зміни</Typography>}
              </Stack>
              {invMsg && <Typography sx={{ mt: 1.5, color: invMsg.startsWith('✅') ? '#39e58c' : '#ff6b6b', fontSize: 14 }}>{invMsg}</Typography>}
            </Box>
          </Box>
        </>)}
      </Paper>

      {/* Звуки-ефекти */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s5'), position: 'relative', display: _zqVis('s5') }} {..._zqDP('s5')}>{_zqGrip('s5')}
        <Typography variant="h6" sx={{ color: '#eee9ff', mb: 1 }}>🔊 Звуки-ефекти</Typography>
        <Typography sx={{ color: '#9a92c4', mb: 2, fontSize: 14 }}>
          Кастомний звук перекриває стандартний (синтезований). Короткі кліпи mp3/wav/ogg.
        </Typography>
        <Stack spacing={1.5}>
          {SND_SLOTS.map(([slot, label]) => {
            const has = !!(cfg.sounds && cfg.sounds[slot]);
            return (
              <Stack key={slot} direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography sx={{ color: '#c9c2e8', minWidth: 250, fontSize: 14 }}>{label}</Typography>
                <Chip size="small" label={has ? 'кастомний' : 'стандартний'}
                  sx={{ bgcolor: has ? 'rgba(57,229,140,.15)' : 'rgba(124,92,255,.15)', color: has ? '#39e58c' : '#cbb8ff' }} />
                {has && (
                  <Button size="small" onClick={() => playAudio('s' + slot, '/api/dashboard/zodiaq/sound/' + slot + '/audio')}
                    sx={{ minWidth: 40, color: playingId === 's' + slot ? '#39e58c' : '#22d3ee' }}>
                    {playingId === 's' + slot ? '⏸' : '▶'}
                  </Button>
                )}
                <Button size="small" variant="outlined" component="label" disabled={busy === 'snd_' + slot}
                  sx={{ color: '#22d3ee', borderColor: 'rgba(150,130,255,.3)' }}>
                  {busy === 'snd_' + slot ? '…' : 'Завантажити'}
                  <input type="file" accept="audio/*" hidden onChange={(e) => uploadSound(slot, e)} />
                </Button>
                {has && <Button size="small" onClick={() => resetSound(slot)} sx={{ color: '#ff6b6b' }}>Скинути</Button>}
              </Stack>
            );
          })}
        </Stack>
      </Paper>

      {/* Фонова музика + гучність */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s6'), position: 'relative', display: _zqVis('s6') }} {..._zqDP('s6')}>{_zqGrip('s6')}
        <Typography variant="h6" sx={{ color: '#eee9ff', mb: 1 }}>🎵 Фонова музика</Typography>
        <Typography sx={{ color: '#9a92c4', mb: 2, fontSize: 14 }}>
          Додай кілька треків — при кожному новому відкритті додатку гратиме випадковий. mp3 до 15 МБ.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button variant="contained" component="label" disabled={busy === 'music'}
            sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>
            {busy === 'music' ? 'Завантаження…' : '＋ Додати трек'}
            <input type="file" accept="audio/*" hidden onChange={uploadMusic} />
          </Button>
          <Chip label={tracks.length + ' трек(ів)'} size="small" sx={{ bgcolor: 'rgba(124,92,255,.2)', color: '#fff' }} />
        </Stack>
        {tracks.length > 0 && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {tracks.map((t) => (
              <Stack key={t.id} direction="row" spacing={2} alignItems="center"
                sx={{ bgcolor: 'rgba(28,24,52,.5)', border: '1px solid rgba(150,130,255,.12)', borderRadius: 2, px: 2, py: 1 }}>
                <Button size="small" onClick={() => playAudio('m' + t.id, '/api/dashboard/zodiaq/music/' + t.id + '/audio')}
                  sx={{ minWidth: 40, color: playingId === 'm' + t.id ? '#39e58c' : '#22d3ee' }}>
                  {playingId === 'm' + t.id ? '⏸' : '▶'}
                </Button>
                <span style={{ fontSize: 15 }}>🎧</span>
                <TextField size="small" variant="standard" value={nameEdits[t.id] ?? t.name} sx={{ flex: 1 }}
                  onChange={(e) => setNameEdits(s => ({ ...s, [t.id]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') renameMusic(t.id, nameEdits[t.id] ?? t.name); }}
                  inputProps={{ style: { color: '#eee9ff', fontSize: 14 } }} />
                {(nameEdits[t.id] != null && nameEdits[t.id] !== t.name) &&
                  <Button size="small" onClick={() => renameMusic(t.id, nameEdits[t.id])} disabled={busy === 'rename' + t.id}
                    sx={{ minWidth: 0, color: '#39e58c' }}>💾</Button>}
                <Typography sx={{ color: '#9a92c4', fontSize: 12 }}>{Math.round((t.size || 0) / 1024)} КБ</Typography>
                <Button size="small" onClick={() => deleteMusic(t.id)} sx={{ color: '#ff6b6b', minWidth: 0 }}>✕</Button>
              </Stack>
            ))}
          </Stack>
        )}
        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />
        <Typography sx={{ color: '#c9c2e8', mb: 1, fontSize: 14 }}>🔈 Гучність музики у додатку: <b>{vol}%</b></Typography>
        <Slider value={vol} min={0} max={100}
          onChange={(e, v) => setVol(v)} onChangeCommitted={(e, v) => saveVolume(v)}
          sx={{ maxWidth: 440, color: '#7c5cff' }} />
        {mediaMsg && <Typography sx={{ color: mediaMsg.startsWith('✅') ? '#39e58c' : '#ff6b6b', fontSize: 14, mt: 1 }}>{mediaMsg}</Typography>}
      </Paper>

      {/* ═══ ФІЛОСОФІЯ ДНЯ ═══ */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s7'), position: 'relative', display: _zqVis('s7') }} {..._zqDP('s7')}>{_zqGrip('s7')}
        <Typography variant="h6" sx={{ color: '#eee9ff', mb: 1 }}>🌌 Філософія дня</Typography>
        <Typography sx={{ color: '#9a92c4', mb: 2, fontSize: 14 }}>
          Твої фрази показуються рандомно (разом з AI-фразами), коли юзер обирає композицію в додатку. Можна вказати підпис.
        </Typography>
        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField label="Філософська фраза" size="small" fullWidth multiline value={philText}
            onChange={(e) => setPhilText(e.target.value)}
            InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField label="Підпис (необов'язково)" size="small" value={philAuthor}
              onChange={(e) => setPhilAuthor(e.target.value)} sx={{ width: 240 }}
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            <Button variant="contained" onClick={savePhil} disabled={!philText.trim()}
              sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>
              {philEditId ? 'Зберегти' : '＋ Додати'}
            </Button>
            {philEditId && <Button size="small" onClick={() => { setPhilEditId(null); setPhilText(''); setPhilAuthor(''); }} sx={{ color: '#9a92c4' }}>Скасувати</Button>}
          </Stack>
        </Stack>
        {philMsg && <Typography sx={{ color: philMsg.startsWith('✅') ? '#39e58c' : '#ff6b6b', fontSize: 13, mb: 1 }}>{philMsg}</Typography>}
        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 1.5 }} />
        {philList.length === 0
          ? <Typography sx={{ color: '#9a92c4', fontSize: 14 }}>Поки що фраз немає. AI-фрази генеруються автоматично.</Typography>
          : (
            <Stack spacing={1}>
              {philList.map((p) => (
                <Stack key={p.id} direction="row" spacing={1.5} alignItems="flex-start"
                  sx={{ bgcolor: 'rgba(28,24,52,.5)', border: '1px solid rgba(150,130,255,.12)', borderRadius: 2, px: 2, py: 1.2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: '#d8d2f0', fontSize: 14, fontStyle: 'italic' }}>{p.text}</Typography>
                    {p.author && <Typography sx={{ color: '#ffd479', fontSize: 12, mt: 0.3 }}>— {p.author}</Typography>}
                  </Box>
                  <Button size="small" onClick={() => editPhil(p)} sx={{ minWidth: 0, color: '#22d3ee' }}>✎</Button>
                  <Button size="small" onClick={() => deletePhil(p.id)} sx={{ minWidth: 0, color: '#ff6b6b' }}>✕</Button>
                </Stack>
              ))}
            </Stack>
          )}
      </Paper>

      {/* ═══ ЗАХИСТ ВІД СКРІНА ═══ */}
      {prem && (
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s8'), position: 'relative', display: _zqVis('s8') }} {..._zqDP('s8')}>{_zqGrip('s8')}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ color: '#eee9ff' }}>🛡 Захист від скріншотів</Typography>
            <Typography sx={{ color: '#9a92c4', fontSize: 13, mt: .5 }}>
              На всіх повідомленнях бота (гороскопи, картки, вітання) вмикається <code>protect_content</code> —
              на мобільному Telegram блокує скріншот і пересилання. Як у Media.
            </Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={!!prem.screen_protection} onChange={toggleScreenProtect} disabled={premBusy} color="warning" />}
            label={<Typography sx={{ color: prem.screen_protection ? '#39e58c' : '#9a92c4', fontWeight: 700 }}>
              {prem.screen_protection ? 'Увімкнено' : 'Вимкнено'}</Typography>} />
        </Stack>
        {premMsg && <Typography sx={{ color: premMsg.startsWith('✅') ? '#39e58c' : '#ff6b6b', fontSize: 13, mt: 1 }}>{premMsg}</Typography>}
      </Paper>
      )}

      {/* ═══ МОНЕТИЗАЦІЯ (преміум) ═══ */}
      {prem && (
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s9'), position: 'relative', display: _zqVis('s9') }} {..._zqDP('s9')}>{_zqGrip('s9')}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ color: '#eee9ff' }}>💎 Монетизація (преміум)</Typography>
          <FormControlLabel
            control={<Switch checked={!!prem.enabled} onChange={toggleEnabled} disabled={premBusy} color="warning" />}
            label={<Typography sx={{ color: prem.enabled ? '#ffd479' : '#9a92c4', fontWeight: 700 }}>
              {prem.enabled ? 'Увімкнено' : 'Вимкнено (все безкоштовно)'}</Typography>}
          />
        </Stack>
        <Typography sx={{ color: '#9a92c4', mb: 2, fontSize: 14 }}>
          Поки вимкнено — усі фічі безкоштовні. Увімкнеш — платними стануть лише позначені нижче фічі.
          Юзер платить на реквізити (карта/крипта), вантажить чек, а ти підтверджуєш у «Заявках» → доступ на N місяців.
        </Typography>

        {/* Перемикачі фіч */}
        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1, mt: 1 }}>Які фічі платні:</Typography>
        <Stack sx={{ mb: 2, opacity: prem.enabled ? 1 : .5 }}>
          {PREM_FEATURES.map(([f, label]) => (
            <FormControlLabel key={f}
              control={<Switch checked={!!prem.features[f]} onChange={() => toggleFeature(f)}
                disabled={premBusy || !prem.enabled} size="small" />}
              label={<Typography sx={{ color: '#c9c2e8', fontSize: 14 }}>{label}</Typography>} />
          ))}
        </Stack>

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        {/* Тарифи */}
        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1 }}>Тарифи (що показувати юзеру):</Typography>
        <Stack spacing={1.2} sx={{ mb: 1.5 }}>
          {(prem.tariffs || []).map((t, i) => (
            <Stack key={i} direction="row" spacing={1.2} alignItems="center" flexWrap="wrap">
              <TextField label="Місяців" type="number" size="small" value={t.months}
                onChange={(e) => setTariff(i, 'months', e.target.value)}
                sx={{ width: 90 }} InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
              <TextField label="Ціна" type="number" size="small" value={t.price}
                onChange={(e) => setTariff(i, 'price', e.target.value)}
                sx={{ width: 100 }} InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
              <TextField label="Валюта" size="small" value={t.currency}
                onChange={(e) => setTariff(i, 'currency', e.target.value)}
                sx={{ width: 90 }} InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
              <FormControlLabel control={<Switch checked={t.enabled !== false} size="small"
                onChange={(e) => setTariff(i, 'enabled', e.target.checked)} />}
                label={<Typography sx={{ color: '#9a92c4', fontSize: 13 }}>показувати</Typography>} />
              <IconButton size="small" onClick={() => removeTariff(i)} sx={{ color: '#ff6b6b' }}>✕</IconButton>
            </Stack>
          ))}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button size="small" onClick={addTariff} sx={{ color: '#22d3ee' }}>＋ Додати тариф</Button>
          <Button size="small" variant="contained" onClick={saveTariffs} disabled={premBusy}
            sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>Зберегти тарифи</Button>
        </Stack>

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        {/* Реквізити + методи */}
        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1 }}>Реквізити для оплати:</Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
          {[['stripe', '💳 Картка онлайн (Stripe)'], ['card', '💳 Карта (реквізити)'], ['crypto', '🪙 Крипта']].map(([m, label]) => (
            <FormControlLabel key={m}
              control={<Switch checked={(prem.methods || []).includes(m)} size="small"
                onChange={() => toggleMethod(m)} disabled={premBusy} />}
              label={<Typography sx={{ color: '#c9c2e8', fontSize: 14 }}>{label}</Typography>} />
          ))}
        </Stack>
        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField label="Номер картки / банк" size="small" fullWidth value={(prem.requisites || {}).card || ''}
            onChange={(e) => setReq('card', e.target.value)} multiline
            InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
          <TextField label="Крипто-адреса (мережа)" size="small" fullWidth value={(prem.requisites || {}).crypto || ''}
            onChange={(e) => setReq('crypto', e.target.value)} multiline
            InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
          <TextField label="Примітка юзеру (напр. «у коментарі вкажи свій Telegram»)" size="small" fullWidth
            value={(prem.requisites || {}).note || ''} onChange={(e) => setReq('note', e.target.value)} multiline
            InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
        </Stack>
        <Button size="small" variant="contained" onClick={saveReq} disabled={premBusy}
          sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>Зберегти реквізити</Button>

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />
        {/* Реферальна програма */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ color: '#c9c2e8', fontWeight: 700 }}>🎁 Реферальна програма</Typography>
          <FormControlLabel
            control={<Switch checked={!!(prem.referral && prem.referral.enabled)} onChange={toggleRef} disabled={premBusy} size="small" color="warning" />}
            label={<Typography sx={{ color: (prem.referral && prem.referral.enabled) ? '#ffd479' : '#9a92c4', fontSize: 13 }}>{(prem.referral && prem.referral.enabled) ? 'Увімкнено' : 'Вимкнено — за друзів нічого'}</Typography>} />
        </Stack>
        {/* ⚠️ 05.10.2026: перемикач вимикає ВСЮ програму, а не лише дні — без нього не записується,
            хто кого привів, і токени теж не нараховуються. Старий підпис казав лише про дні,
            і так реферал мовчки не працював з 02.08 до 05.10.2026. */}
        <Typography sx={{ color: '#9a92c4', fontSize: 13, mb: 1 }}>
          Вмикає <b>всю</b> реферальну програму. За кожного нового друга: токени обом (скільки — у секції «💎 Токени (гаманець)» на цій же вкладці:
          «За приведеного друга» і «Новачку за запрошенням») і, якщо нижче більше 0, ще й дні Преміуму тому, хто запросив.
          Вимкнеш — за друзів не нараховується <b>нічого</b>, і навіть не запамʼятовується, хто кого привів.
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField label="Дні Преміуму / друга (0 = лише токени)" type="number" size="small" value={(prem.referral || {}).bonus_days ?? 7}
            onChange={(e) => setRefDays(e.target.value)} sx={{ width: 150 }}
            InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
          <Button size="small" variant="contained" onClick={saveRefDays} disabled={premBusy}
            sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>Зберегти</Button>
        </Stack>

        {premMsg && <Typography sx={{ color: premMsg.startsWith('✅') ? '#39e58c' : '#ff6b6b', fontSize: 14, mt: 1.5 }}>{premMsg}</Typography>}
      </Paper>
      )}

      {/* ═══ ЗАЯВКИ НА ПРЕМІУМ ═══ */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s10'), position: 'relative', display: _zqVis('s10') }} {..._zqDP('s10')}>{_zqGrip('s10')}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#eee9ff' }}>
            🧾 Заявки на преміум
            {pendingCount > 0 && <Chip label={pendingCount + ' нових'} size="small"
              sx={{ ml: 1, bgcolor: 'rgba(255,212,121,.2)', color: '#ffd479' }} />}
          </Typography>
          <Button size="small" onClick={loadPrem} sx={{ color: '#22d3ee' }}>↻ Оновити</Button>
        </Stack>
        {payments.length === 0
          ? <Typography sx={{ color: '#9a92c4' }}>Заявок ще немає.</Typography>
          : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['#', 'Юзер', 'Тариф', 'Сума', 'Метод', 'Статус', 'Дата', 'Дії'].map(h =>
                      <TableCell key={h} sx={{ color: '#9a92c4', borderColor: 'rgba(150,130,255,.12)', fontSize: 11, textTransform: 'uppercase' }}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((p) => {
                    const sc = STATUS_CHIP[p.status] || [p.status, 'rgba(124,92,255,.15)', '#cbb8ff'];
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell sx={{ color: '#9a92c4', borderColor: 'rgba(150,130,255,.08)' }}>{p.id}</TableCell>
                        <TableCell sx={{ color: '#eee9ff', borderColor: 'rgba(150,130,255,.08)', fontSize: 13 }}>
                          {p.first_name || '—'}{p.username ? ' @' + p.username : ''}<br />
                          <span style={{ color: '#9a92c4', fontSize: 11 }}>{p.telegram_id}</span>
                        </TableCell>
                        <TableCell sx={{ color: p.tariff_id === 'donate' ? '#ffd479' : '#c9c2e8', borderColor: 'rgba(150,130,255,.08)', fontSize: 13 }}>{p.tariff_id === 'donate' ? '💛 Донат' : (p.months + ' міс')}</TableCell>
                        <TableCell sx={{ color: '#ffd479', borderColor: 'rgba(150,130,255,.08)', fontSize: 13 }}>{p.amount}</TableCell>
                        <TableCell sx={{ color: '#c9c2e8', borderColor: 'rgba(150,130,255,.08)', fontSize: 13 }}>{p.method || '—'}</TableCell>
                        <TableCell sx={{ borderColor: 'rgba(150,130,255,.08)' }}>
                          <Chip label={sc[0]} size="small" sx={{ bgcolor: sc[1], color: sc[2], fontSize: 11 }} /></TableCell>
                        <TableCell sx={{ color: '#9a92c4', borderColor: 'rgba(150,130,255,.08)', fontSize: 12 }}>
                          {p.created_at ? new Date(p.created_at).toLocaleString('uk-UA') : ''}</TableCell>
                        <TableCell sx={{ borderColor: 'rgba(150,130,255,.08)' }}>
                          <Stack direction="row" spacing={.5}>
                            {p.has_receipt && <Button size="small" onClick={() => viewReceipt(p.id)} sx={{ color: '#22d3ee', minWidth: 0 }}>🧾</Button>}
                            {p.status === 'pending' && <>
                              <Button size="small" onClick={() => decide(p.id, true, p.months)} disabled={premBusy}
                                sx={{ color: '#39e58c', minWidth: 0 }}>✔</Button>
                              <Button size="small" onClick={() => decide(p.id, false)} disabled={premBusy}
                                sx={{ color: '#ff6b6b', minWidth: 0 }}>✖</Button>
                            </>}
                            <Button size="small" onClick={() => deletePay(p.id)} disabled={premBusy}
                              sx={{ color: '#ff6b6b', minWidth: 0 }} title="Видалити">🗑</Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </Paper>

      {/* Перегляд чека */}
      <Dialog open={!!receipt} onClose={() => { if (receipt) URL.revokeObjectURL(receipt.url); setReceipt(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#140f24', color: '#eee9ff' }}>Чек заявки #{receipt && receipt.pid}</DialogTitle>
        <DialogContent sx={{ bgcolor: '#140f24', textAlign: 'center' }}>
          {receipt && <img src={receipt.url} alt="чек" style={{ maxWidth: '100%', borderRadius: 8 }} />}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#140f24' }}>
          <Button onClick={() => { if (receipt) URL.revokeObjectURL(receipt.url); setReceipt(null); }} sx={{ color: '#22d3ee' }}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ КОНСУЛЬТАЦІЯ З ЖИВИМ АСТРОЛОГОМ ═══ */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s14'), position: 'relative', display: _zqVis('s14') }} {..._zqDP('s14')}>{_zqGrip('s14')}
        <Typography variant="h6" sx={{ color: '#eee9ff', mb: 1 }}>💎 Токени (гаманець)</Typography>
        <Typography sx={{ color: '#9a92c4', mb: 2, fontSize: 14 }}>
          Юзер купує токени і витрачає їх на пункти розбору карти й розбору на двох.
          Змінені тут числа діють одразу, без деплою.
        </Typography>

        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {[['на балансах', zqCr?.on_balances], ['юзерів із токенами', zqCr?.users_with_credits],
            ['куплено всього', zqCr?.bought_total], ['витрачено', zqCr?.spent_total],
            ['виручка, €', zqCr?.revenue_eur]].map(([l, v]) => (
              <Chip key={l} label={`${l}: ${v ?? '—'}`} size="small"
                sx={{ bgcolor: 'rgba(255,212,121,.12)', color: '#ffe9b8', fontWeight: 700 }} />
            ))}
        </Stack>

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1 }}>Ціни:</Typography>
        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {ZQ_CREDIT_FIELDS.map(([k, label]) => (
            <TextField key={k} label={label} type="number" size="small"
              value={zqCrVals[k] ?? ''}
              onChange={(e) => setZqCrVals({ ...zqCrVals, [k]: e.target.value })}
              sx={{ width: 235 }} InputLabelProps={{ sx: { color: '#9a92c4' } }}
              inputProps={{ style: { color: '#eee9ff' } }}
              helperText={zqCr?.defaults?.[k] != null ? ('за замовчуванням ' + zqCr.defaults[k]) : ' '}
              FormHelperTextProps={{ sx: { color: String(zqCrVals[k]) !== String(zqCr?.defaults?.[k]) ? '#ffd479' : '#6a6390' } }} />
          ))}
        </Stack>

        {/* 🔮 2026-09-11: рівні розбору — тими самими словами, що бачить юзер у
            застосунку. Показуємо ОБИДВІ ціни: звичайну і преміум, бо саме через
            знижку в додатку видно 1 / 1 / 2 💎, а не 1 / 2 / 4. */}
        {(zqCr?.depth?.tiers || []).length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(124,92,255,.07)', border: '1px solid rgba(150,130,255,.25)' }}>
            <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1 }}>
              Рівні розбору — як їх бачить юзер:
            </Typography>
            <Stack spacing={1}>
              {zqCr.depth.tiers.map((t) => (
                <Stack key={t.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ color: '#eee9ff', fontWeight: 700, minWidth: 128 }}>
                    {{ 1: '📄', 2: '📖', 3: '📚' }[t.id] || ''} {t.name}
                  </Typography>
                  <Chip size="small" label={t.cost + ' 💎'}
                    sx={{ bgcolor: 'rgba(124,92,255,.25)', color: '#cbb8ff', fontWeight: 700 }} />
                  {t.cost_premium !== t.cost && (
                    <Chip size="small" label={'преміум ' + t.cost_premium + ' 💎'}
                      sx={{ bgcolor: 'rgba(255,212,121,.18)', color: '#ffd479' }} />
                  )}
                  <TextField select size="small" value={t.model || ''}
                    onChange={(e) => saveTierModel(t.id, e.target.value)}
                    SelectProps={{ native: true }} sx={{ width: 170, '& select': { color: '#eee9ff', padding: '6px 8px' } }}>
                    {(zqCr.depth.models_allowed || []).map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </TextField>
                  <Typography sx={{ color: '#8a83b0', fontSize: 12 }}>{t.len}</Typography>
                </Stack>
              ))}
            </Stack>
            <Typography sx={{ color: '#8a83b0', fontSize: 12.5, mt: 1 }}>
              Преміум бачить −{zqCr.depth.premium_off_pct}% (але не менше 1 💎) — тому в застосунку
              у підписника стоїть {(zqCr.depth.tiers || []).map((t) => t.cost_premium).join(' / ')} 💎,
              а перекреслене число поруч — повна ціна.
              Модель зберігається одразу після вибору й перевіряється живим викликом.
            </Typography>
          </Box>
        )}

        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1, mt: 1 }}>Скільки коштує НАМ одна відповідь:</Typography>
        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {ZQ_AI_FIELDS.map(([k, label]) => (
            <TextField key={k} label={label} type="number" size="small"
              value={zqAiVals[k] ?? ''}
              onChange={(e) => setZqAiVals({ ...zqAiVals, [k]: e.target.value })}
              sx={{ width: 290 }} InputLabelProps={{ sx: { color: '#9a92c4' } }}
              inputProps={{ step: '0.0001', style: { color: '#eee9ff' } }}
              helperText={'модель ' + ((zqCr?.ai_models || {})[k] || '—')
                + (zqCr?.ai_defaults?.[k] != null ? (' · типово ' + zqCr.ai_defaults[k]) : '')}
              FormHelperTextProps={{ sx: { color: '#6a6390' } }} />
          ))}
        </Stack>

        {/* Що числа означають у грошах — головне, чого з них самих не видно */}
        <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(255,212,121,.07)', color: '#ffe9b8', border: '1px solid rgba(255,212,121,.25)' }}>
          €1 = <b>{zqCrVals.credits_per_eur}</b> токенів · один пункт = €{(Number(zqCrVals.cost_item) / (Number(zqCrVals.credits_per_eur) || 1)).toFixed(2)}<br />
          уся карта ({ZQ_ITEMS_FULL} пунктів) ≈ <b>€{(Number(zqCrVals.cost_item) * ZQ_ITEMS_FULL / (Number(zqCrVals.credits_per_eur) || 1)).toFixed(2)}</b><br />
          подарунок новому юзеру коштує нам €{(Number(zqCrVals.starter_credits) / (Number(zqCrVals.credits_per_eur) || 1)).toFixed(2)} ·
          пункт найглибшого рівня = €{(Number(zqCrVals.cost_item) * Number(zqCrVals.tier3_mult) / (Number(zqCrVals.credits_per_eur) || 1)).toFixed(2)}<br />
          приведений друг обходиться в <b>€{((Number(zqCrVals.ref_credits) + Number(zqCrVals.ref_welcome_credits)) / (Number(zqCrVals.credits_per_eur) || 1)).toFixed(2)}</b>
        </Alert>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Button variant="contained" color="warning" onClick={saveCredits} disabled={zqCrBusy}>Зберегти</Button>
          <Button variant="outlined" onClick={() => { setZqCrMsg(''); loadCredits(); }} disabled={zqCrBusy}>↻ Перечитати</Button>
          {zqCrMsg && <Typography sx={{ color: zqCrMsg.startsWith('✅') ? '#7fe3b0' : '#ff6b6b', fontSize: 14 }}>{zqCrMsg}</Typography>}
        </Stack>

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        {/* ── Способи оплати ── */}
        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1 }}>Способи оплати:</Typography>
        <Typography sx={{ color: '#9a92c4', fontSize: 13, mb: 1.5 }}>
          Спосіб показується юзеру лише тоді, коли заповнені його реквізити.
          Порожнє поле = спосіб просто не пропонується.
        </Typography>
        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {[['topup_packs', 'Суми-підказки (через кому)', 260],
            ['topup_bonus_pct', 'Бонус за прямий переказ, %', 210],
            ['stripe_min_eur', 'Stripe показувати від, €', 200],
            ['fx_margin_percent', 'Маржа курсу, %', 170]].map(([k, label, w]) => (
              <TextField key={k} label={label} size="small" value={zqPay[k] ?? ''}
                onChange={(e) => setZqPay({ ...zqPay, [k]: e.target.value })}
                sx={{ width: w }} InputLabelProps={{ sx: { color: '#9a92c4' } }}
                inputProps={{ style: { color: '#eee9ff' } }} />
            ))}
        </Stack>
        <Alert severity="info" sx={{ mb: 1.5, bgcolor: 'rgba(120,190,255,.07)', color: '#cfe6ff', border: '1px solid rgba(120,190,255,.25)' }}>
          Курс: <b>1€ = {zqPay.rate ?? '—'}₴</b>{zqPay.rate_live === false ? ' · не свіжий, джерело мовчить' : ''}
          <br />Джерело те саме, що в «Пакети / Тарифи». Маржа 0 — числа збігаються один в один.
        </Alert>
        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {[['card', 'Монобанк — картка (₴)', 260], ['revolut', 'Revolut (€)', 260],
            ['crypto', 'Криптогаманець', 300], ['note', 'Примітка юзеру', 300]].map(([k, label, w]) => (
              <TextField key={k} label={label} size="small" value={(zqPay.requisites || {})[k] ?? ''}
                onChange={(e) => setZqPay({ ...zqPay, requisites: { ...(zqPay.requisites || {}), [k]: e.target.value } })}
                sx={{ width: w }} InputLabelProps={{ sx: { color: '#9a92c4' } }}
                inputProps={{ style: { color: '#eee9ff' } }} />
            ))}
        </Stack>
        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1, mt: 1 }}>Банка Монобанку (автозарахування):</Typography>
        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <TextField label="Посилання на банку (send.monobank.ua/jar/…)" size="small"
            value={zqPay.mono_jar_url_uah ?? ''}
            onChange={(e) => setZqPay({ ...zqPay, mono_jar_url_uah: e.target.value })}
            sx={{ width: 340 }} InputLabelProps={{ sx: { color: '#9a92c4' } }}
            inputProps={{ style: { color: '#eee9ff' } }} />
          <TextField label="id банки (для читання виписки)" size="small"
            value={zqPay.mono_jar_id_uah ?? ''}
            onChange={(e) => setZqPay({ ...zqPay, mono_jar_id_uah: e.target.value })}
            sx={{ width: 280 }} InputLabelProps={{ sx: { color: '#9a92c4' } }}
            inputProps={{ style: { color: '#eee9ff' } }} />
          <TextField label={zqPay.mono_token_set ? 'Токен monobank — заданий, введи новий щоб змінити' : 'Токен monobank Personal API'}
            size="small" type="password" value={zqPay.mono_token ?? ''}
            onChange={(e) => setZqPay({ ...zqPay, mono_token: e.target.value })}
            sx={{ width: 340 }} InputLabelProps={{ sx: { color: '#9a92c4' } }}
            inputProps={{ style: { color: '#eee9ff' }, autoComplete: 'new-password' }}
            helperText="Назад не показується. Порожнє поле нічого не стирає."
            FormHelperTextProps={{ sx: { color: '#6a6390' } }} />
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Button variant="contained" color="warning" onClick={savePay}>Зберегти способи</Button>
          <Button variant="outlined" onClick={importMonoToken}>Взяти токен із PlayLanguage</Button>
          <Button variant="outlined" onClick={showJars}>Показати мої банки</Button>
          <Button variant="outlined" onClick={monoPoll}>Перевірити виписку зараз</Button>
          {zqPayMsg && <Typography sx={{ color: zqPayMsg.startsWith('✅') ? '#7fe3b0' : '#ff6b6b', fontSize: 14 }}>{zqPayMsg}</Typography>}
        </Stack>
        {!!zqJars.length && (
          <Alert severity="success" sx={{ mb: 2, bgcolor: 'rgba(127,227,176,.08)', color: '#c6f2dd', border: '1px solid rgba(127,227,176,.3)' }}>
            <b>Твої банки — вибери потрібну і встав її id у поле вище:</b>
            {zqJars.map((j) => (
              <div key={j.id} style={{ marginTop: 8 }}>
                {j.title} · {j.ccy} · баланс {j.balance}
                <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: '#eee9ff' }}>{j.id}</div>
              </div>
            ))}
          </Alert>
        )}

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        {/* ── Заявки на поповнення ── */}
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography sx={{ color: '#c9c2e8', fontWeight: 700 }}>
            Заявки на поповнення{zqClaims.length ? (' · ' + zqClaims.length) : ''}:
          </Typography>
          <Button variant="outlined" size="small" onClick={loadClaims}>↻ Оновити</Button>
          {zqClaimMsg && <Typography sx={{ color: zqClaimMsg.startsWith('✅') ? '#7fe3b0' : '#c9c2e8', fontSize: 13 }}>{zqClaimMsg}</Typography>}
        </Stack>
        <Typography sx={{ color: '#9a92c4', fontSize: 13, mb: 1.5 }}>
          Прямий переказ іде повз нас — підтвердити його можеш лише ти, бо лише ти бачиш свій рахунок.
          Банка зараховується сама і сюди не потрапляє.
        </Typography>
        {!zqClaims.length ? (
          <Typography sx={{ color: '#9a92c4', fontSize: 14 }}>Заявок немає.</Typography>
        ) : (
          <Stack spacing={1.2}>
            {zqClaims.map((x) => (
              <Stack key={x.id} direction="row" spacing={1.2} alignItems="center" flexWrap="wrap" useFlexGap
                sx={{ p: 1.2, border: '1px solid rgba(150,130,255,.2)', borderRadius: 2, bgcolor: 'rgba(255,255,255,.03)' }}>
                <Typography sx={{ color: '#eee9ff', fontSize: 14, fontWeight: 700, minWidth: 120 }}>
                  €{x.eur} → {x.credits} 💎</Typography>
                <Typography sx={{ color: '#c9c2e8', fontSize: 13, flex: 1, minWidth: 160 }}>
                  {(x.first_name || '') + (x.username ? (' @' + x.username) : '') || x.tid}
                  <span style={{ color: '#6a6390' }}> · {x.tid} · {x.method}</span>
                </Typography>
                {/* ⚠️ window.open не надішле Bearer-токен, тож картинку
                    тягнемо запитом і відкриваємо вже отримане. */}
                {x.has_receipt && <Button size="small" variant="outlined"
                  onClick={async () => {
                    try {
                      const r = await fetch('/api/dashboard/zodiaq/topup/receipt/' + x.id, { headers: AUTH() });
                      if (!r.ok) { setZqClaimMsg('Чек не відкрився'); return; }
                      const b = await r.blob();
                      window.open(URL.createObjectURL(b), '_blank');
                    } catch (e) { setZqClaimMsg('Чек не відкрився'); }
                  }}>Чек</Button>}
                <Button size="small" variant="contained" color="success"
                  onClick={() => decideClaim(x.id, true)}>Зарахувати</Button>
                <Button size="small" variant="outlined" color="error"
                  onClick={() => decideClaim(x.id, false)}>Відхилити</Button>
              </Stack>
            ))}
          </Stack>
        )}

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        {/* ── Безкоштовні пояснення пунктів ── */}
        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1 }}>Пояснення пунктів:</Typography>
        <Typography sx={{ color: '#9a92c4', fontSize: 13, mb: 1.5 }}>
          Безкоштовний вступ «що це, звідки і навіщо» — саме він продає платний розбір.
          Пишеться сам при першому перегляді й далі читається з бази, токенів не коштує.
          Скидання потрібне, лише якщо текст вийшов невдалим: він напишеться наново.
          Нічого вводити не треба — тисни «Читати», і якщо текст поганий, тут же «Скинути».
          Червоні — текст вийшов не про ту тему; вони юзерам НЕ показуються і чекають скидання.
        </Typography>
        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }} alignItems="center">
          {(zqExpl && zqExpl.by_lang ? zqExpl.by_lang : []).map((x) => (
            <Chip key={x.lang} size="small"
              label={`${x.lang}: ${x.count}` + (x.bad ? ` · ${x.bad} не про ту тему` : '')}
              sx={{
                bgcolor: x.bad ? 'rgba(255,107,107,.18)' : 'rgba(124,92,255,.16)',
                color: x.bad ? '#ff9b9b' : '#c9b8ff', fontWeight: 700,
              }} />
          ))}
          {!(zqExpl && zqExpl.by_lang && zqExpl.by_lang.length) &&
            <Typography sx={{ color: '#9a92c4', fontSize: 13 }}>Ще жодного — з'являться після перших переглядів.</Typography>}
          <Button variant="outlined" size="small" onClick={loadExplStats}>↻ Оновити</Button>
        </Stack>

        {/* ⚠️ Текст цілком, а не обрізаний: «поганий текст» видно лише з тексту. */}
        {zqExplList.length > 0 && (
          <Stack spacing={0.8} sx={{ mb: 2 }}>
            {zqExplList.map((x) => (
              <Box key={x.slot + '|' + x.lang}
                sx={{
                  border: '1px solid ' + (x.ok === false ? 'rgba(255,107,107,.55)' : 'rgba(150,130,255,.16)'),
                  bgcolor: x.ok === false ? 'rgba(255,107,107,.07)' : 'transparent',
                  borderRadius: 2, p: 1.2,
                }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                    <Typography sx={{ color: '#eee9ff', fontWeight: 700, fontSize: 14 }}>{x.slot}</Typography>
                    {/* Тема — те, про що текст МАЄ бути. Без неї «поганий текст» не з чим звірити. */}
                    {x.subject && <Typography sx={{ color: '#9a92c4', fontSize: 12 }}>{x.subject}</Typography>}
                  </Box>
                  {x.ok === false && (
                    <Chip size="small" label="не про ту тему — не показується юзерам"
                      sx={{ bgcolor: 'rgba(255,107,107,.18)', color: '#ff9b9b', fontWeight: 700 }} />
                  )}
                  <Typography sx={{ color: '#9a92c4', fontSize: 12 }}>{x.chars} симв.</Typography>
                  <Button size="small" variant="text"
                    onClick={() => setZqExplOpen(zqExplOpen === x.slot ? '' : x.slot)}>
                    {zqExplOpen === x.slot ? 'Згорнути' : 'Читати'}
                  </Button>
                  <Button size="small" variant="outlined" color="error"
                    onClick={() => resetExpl(x.slot)}>Скинути</Button>
                </Stack>
                {zqExplOpen === x.slot && (
                  <Typography sx={{ color: '#c9c2e8', fontSize: 13, mt: 1, whiteSpace: 'pre-wrap' }}>
                    {x.body}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1 }}>Ручне нарахування:</Typography>
        <Typography sx={{ color: '#9a92c4', mb: 1.5, fontSize: 13 }}>
          Повернути гроші, подарувати, виправити збій. Додатне число нараховує, від’ємне — списує.
          Операція лягає в книгу з поміткою.
        </Typography>
        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <TextField label="Telegram ID" size="small" value={zqGrantTid} onChange={(e) => setZqGrantTid(e.target.value)}
            sx={{ width: 180 }} InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
          <TextField label="Скільки (можна з мінусом)" type="number" size="small" value={zqGrantAmt}
            onChange={(e) => setZqGrantAmt(e.target.value)}
            sx={{ width: 215 }} InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
          <TextField label="Помітка" size="small" value={zqGrantNote} onChange={(e) => setZqGrantNote(e.target.value)}
            sx={{ width: 260 }} InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
          <Button variant="contained" onClick={grantCredits} disabled={zqCrBusy}>Провести</Button>
        </Stack>
        {zqGrantMsg && <Typography sx={{ color: zqGrantMsg.startsWith('✅') ? '#7fe3b0' : '#ff6b6b', fontSize: 14, mb: 1.5 }}>{zqGrantMsg}</Typography>}

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography sx={{ color: '#c9c2e8', fontWeight: 700 }}>Книга операцій:</Typography>
          <TextField label="Telegram ID (не обов’язково)" size="small" value={zqCrFilter}
            onChange={(e) => setZqCrFilter(e.target.value)}
            sx={{ width: 240 }} InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
          <Button variant="outlined" size="small" onClick={() => loadCrLedger(zqCrFilter.trim())}>Показати</Button>
        </Stack>
        {!zqCrLedger.length ? (
          <Typography sx={{ color: '#9a92c4', fontSize: 14 }}>Операцій немає.</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>{['Коли', 'Юзер', '±', 'Стало', 'За що', 'Помітка'].map((h) => (
                  <TableCell key={h} sx={{ color: '#9a92c4', bgcolor: '#1a1730', fontSize: 12 }}>{h}</TableCell>))}
                </TableRow>
              </TableHead>
              <TableBody>
                {zqCrLedger.map((x, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ color: '#9a92c4', fontSize: 13 }}>
                      {x.at ? new Date(x.at).toLocaleString('uk-UA') : ''}</TableCell>
                    <TableCell sx={{ color: '#c9c2e8', fontSize: 13 }}>
                      {/* імʼя й username приходять з бекенду: голий id не каже, хто це */}
                      {(x.first_name || x.username)
                        ? <>{x.first_name || ''}{x.username ? <span style={{ color: '#8a83b0' }}> @{x.username}</span> : ''}
                            <div style={{ color: '#6a6390', fontSize: 11 }}>{x.tid}</div></>
                        : x.tid}
                    </TableCell>
                    <TableCell sx={{ color: x.delta > 0 ? '#7fe3b0' : '#9a92c4', fontWeight: 700, fontSize: 13 }}>
                      {x.delta > 0 ? '+' : ''}{x.delta}</TableCell>
                    <TableCell sx={{ color: '#eee9ff', fontSize: 13 }}>{x.after}</TableCell>
                    <TableCell sx={{ color: '#c9c2e8', fontSize: 13 }}>{ZQ_CREDIT_REASONS[x.reason] || x.reason}</TableCell>
                    <TableCell sx={{ color: '#9a92c4', fontSize: 13 }}>{x.note || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      </Paper>

      {/* ═══ 💰 ДОХОДИ — окрема вкладка, перша ═══ */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s15'), position: 'relative', display: _zqVis('s15') }} {..._zqDP('s15')}>{_zqGrip('s15')}
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography variant="h6" sx={{ color: '#eee9ff' }}>💰 Хто скільки приніс</Typography>
          <Button variant="outlined" size="small" onClick={loadEcon}>↻ Перерахувати</Button>
        </Stack>
        <Typography sx={{ color: '#9a92c4', fontSize: 13, mb: 1.5 }}>
          «Відповідей» — скільки разів ми зверталися до моделі за цього юзера, разом із
          безкоштовними денними гороскопами. Той, хто нічого не купив, теж нам коштує —
          без цього рядка картина була б прикрашеною.
          <br />Вартість однієї відповіді задається у вкладці «💎 Монетизація».
        </Typography>
        {!(zqEcon && zqEcon.rows && zqEcon.rows.length) ? (
          <Typography sx={{ color: '#9a92c4', fontSize: 14 }}>Даних ще немає.</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>{['Юзер', 'Заплатив €', 'Токенів витрачено', 'Баланс',
                  'Відповідей', 'з них глибоких', 'Наша вартість €', 'Прибуток €'].map((h) => (
                    <TableCell key={h} sx={{ color: '#9a92c4', bgcolor: '#1a1730', fontSize: 12 }}>{h}</TableCell>))}
                </TableRow>
              </TableHead>
              <TableBody>
                {zqEcon.rows.map((u, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ color: '#c9c2e8', fontSize: 13 }}>
                      {(u.first_name || u.username)
                        ? <>{u.first_name || ''}{u.username ? <span style={{ color: '#8a83b0' }}> @{u.username}</span> : ''}
                            <div style={{ color: '#6a6390', fontSize: 11 }}>{u.tid}</div></>
                        : u.tid}
                    </TableCell>
                    <TableCell sx={{ color: u.paid_eur > 0 ? '#7fe3b0' : '#6a6390', fontWeight: 700, fontSize: 13 }}>
                      {u.paid_eur.toFixed(2)}</TableCell>
                    <TableCell sx={{ color: '#c9c2e8', fontSize: 13 }}>{u.spent}</TableCell>
                    <TableCell sx={{ color: '#ffe9b8', fontSize: 13 }}>{u.balance}</TableCell>
                    <TableCell sx={{ color: '#c9c2e8', fontSize: 13 }}>{u.answers + u.free}</TableCell>
                    <TableCell sx={{ color: '#c9c2e8', fontSize: 13 }}>{u.deep}</TableCell>
                    <TableCell sx={{ color: '#9a92c4', fontSize: 13 }}>{u.cost_eur.toFixed(4)}</TableCell>
                    <TableCell sx={{ color: u.profit_eur >= 0 ? '#7fe3b0' : '#ff6b6b', fontWeight: 700, fontSize: 13 }}>
                      {u.profit_eur.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s11'), position: 'relative', display: _zqVis('s11') }} {..._zqDP('s11')}>{_zqGrip('s11')}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ color: '#eee9ff' }}>🧑‍🏫 Консультація з астрологом</Typography>
          <FormControlLabel
            control={<Switch checked={consEnabled} onChange={toggleConsult} color="warning" />}
            label={<Typography sx={{ color: consEnabled ? '#ffd479' : '#9a92c4', fontWeight: 700 }}>
              {consEnabled ? 'Увімкнено' : 'Вимкнено'}</Typography>} />
        </Stack>
        <Typography sx={{ color: '#9a92c4', mb: 2, fontSize: 14 }}>
          Юзер обирає астролога і замовляє консультацію. Преміум — безкоштовно (заявка + натальні дані
          одразу приходять тобі). Безкоштовний тариф — платить на реквізити преміуму й вантажить чек,
          який ти підтверджуєш нижче. Оплата йде на ті ж реквізити, що й у монетизації.
        </Typography>

        {/* Список астрологів */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ color: '#c9c2e8', fontWeight: 700 }}>Астрологи:</Typography>
          <Button size="small" onClick={() => editConsult(null)} sx={{ color: '#22d3ee' }}>＋ Додати астролога</Button>
        </Stack>
        {consultants.length === 0
          ? <Typography sx={{ color: '#9a92c4', mb: 1.5 }}>Ще немає астрологів. Додай першого 🔮</Typography>
          : <Stack spacing={1.2} sx={{ mb: 1.5 }}>
              {consultants.map((cs) => (
                <Stack key={cs.id} direction="row" spacing={1.5} alignItems="center"
                  sx={{ p: 1.2, borderRadius: 2, bgcolor: 'rgba(28,24,52,.5)', border: '1px solid rgba(150,130,255,.15)', opacity: cs.active ? 1 : .5 }}>
                  <Avatar src={cs.has_photo ? ('/api/dashboard/zodiaq/consultant-photo/' + cs.id) : undefined}
                    sx={{ width: 46, height: 46, bgcolor: 'rgba(124,92,255,.25)' }}>🔮</Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: '#eee9ff', fontWeight: 600, fontSize: 14 }}>
                      {cs.name}{!cs.active && <Chip label="прихований" size="small" sx={{ ml: 1, bgcolor: 'rgba(150,130,255,.15)', color: '#9a92c4', fontSize: 10 }} />}</Typography>
                    {cs.spec && <Typography sx={{ color: '#9a92c4', fontSize: 12 }}>{cs.spec}</Typography>}
                    {cs.price && <Typography sx={{ color: '#ffd479', fontSize: 12 }}>{cs.price}</Typography>}
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {cs.rating_count > 0 && <Chip label={'⭐ ' + Number(cs.rating_avg).toFixed(1) + ' (' + cs.rating_count + ')'} size="small" sx={{ bgcolor: 'rgba(255,212,121,.15)', color: '#ffd479', fontSize: 10 }} />}
                      <Chip label={cs.tg_id ? '🧑‍🏫 кабінет активний' : (cs.tg_username ? '⏳ хай напише боту /start' : 'без @username')} size="small"
                        sx={{ bgcolor: cs.tg_id ? 'rgba(57,229,140,.15)' : 'rgba(150,130,255,.12)', color: cs.tg_id ? '#39e58c' : '#9a92c4', fontSize: 10 }} />
                    </Stack>
                  </Box>
                  <Button size="small" onClick={() => editConsult(cs)} sx={{ color: '#22d3ee', minWidth: 0 }}>✎</Button>
                  <Button size="small" onClick={() => deleteConsult(cs.id)} sx={{ color: '#ff6b6b', minWidth: 0 }}>🗑</Button>
                </Stack>
              ))}
            </Stack>}

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        {/* Пакети тепер per-consultant — редагуються в картці консультанта (кнопка ✎). Тут — глобальні налаштування. */}
        <Typography sx={{ color: '#9a92c4', fontSize: 13, mb: 1 }}>💡 Ціни/пакети кожного консультанта — усередині його картки (кнопка ✎ вгорі). Кожен пакет: тривалість + ціна + валюта.</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <TextField label="Ліміт Преміум-чату (хв)" type="number" size="small" value={consPremMin}
            onChange={(e) => setConsPremMin(e.target.value)} sx={{ width: 180 }}
            InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
          <Button size="small" variant="contained" onClick={savePremMin}
            sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>Зберегти</Button>
        </Stack>

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        {/* Умови для заявок консультантів */}
        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1 }}>📄 Умови співпраці (бачить заявник у формі)</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <TextField label="Комісія %" type="number" size="small" sx={{ width: 110 }} value={applyTerms.commission}
            onChange={(e) => setApplyTerms({ ...applyTerms, commission: e.target.value })}
            InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
          <FormControlLabel control={<Switch checked={!!applyTerms.enabled} size="small"
            onChange={(e) => setApplyTerms({ ...applyTerms, enabled: e.target.checked })} />}
            label={<Typography sx={{ color: '#c9c2e8', fontSize: 13 }}>кнопка заявки видима юзерам</Typography>} />
        </Stack>
        <TextField label="Текст умов (показується в оверлеї)" size="small" fullWidth multiline minRows={4} value={applyTerms.terms || ''}
          onChange={(e) => setApplyTerms({ ...applyTerms, terms: e.target.value })} sx={{ mb: 1 }}
          InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
        <Button size="small" variant="contained" onClick={saveApplyTerms}
          sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700, mb: 2 }}>Зберегти умови</Button>

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        {/* Черга заявок стати консультантом */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ color: '#c9c2e8', fontWeight: 700 }}>
            🧑‍🏫 Заявки стати консультантом
            {consApps.filter(a => a.status === 'pending').length > 0 && <Chip label={consApps.filter(a => a.status === 'pending').length + ' нових'} size="small" sx={{ ml: 1, bgcolor: 'rgba(255,212,121,.2)', color: '#ffd479' }} />}
          </Typography>
        </Stack>
        {consApps.length === 0
          ? <Typography sx={{ color: '#9a92c4', mb: 2 }}>Заявок ще немає.</Typography>
          : <Stack spacing={1} sx={{ mb: 2 }}>
              {consApps.map((a) => (
                <Stack key={a.id} direction="row" spacing={1} alignItems="center"
                  sx={{ p: 1.2, borderRadius: 2, bgcolor: 'rgba(28,24,52,.5)', border: '1px solid rgba(150,130,255,.15)' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: '#eee9ff', fontSize: 14, fontWeight: 600 }}>{a.first_name} {a.last_name}{a.username ? ' @' + a.username : ''}</Typography>
                    <Typography sx={{ color: '#9a92c4', fontSize: 12 }}>{a.sphere}{a.experience ? ' · ' + a.experience : ''}{a.city ? ' · ' + a.city : ''}</Typography>
                  </Box>
                  <Chip label={(STATUS_CHIP[a.status] || ['?', '', '#9a92c4'])[0]} size="small" sx={{ bgcolor: (STATUS_CHIP[a.status] || ['', 'rgba(150,130,255,.12)'])[1], color: (STATUS_CHIP[a.status] || ['', '', '#9a92c4'])[2], fontSize: 11 }} />
                  <Button size="small" onClick={() => setAppView(a)} sx={{ color: '#22d3ee', minWidth: 0 }}>👁</Button>
                  {a.status === 'pending' && <>
                    <Button size="small" onClick={() => decideApp(a.id, true)} disabled={consBusy} sx={{ color: '#39e58c', minWidth: 0 }}>✔</Button>
                    <Button size="small" onClick={() => decideApp(a.id, false, window.prompt('Причина відмови (необов\'язково):') || '')} disabled={consBusy} sx={{ color: '#ff6b6b', minWidth: 0 }}>✖</Button>
                  </>}
                  <Button size="small" onClick={() => deleteApp(a.id)} disabled={consBusy} sx={{ color: '#ff6b6b', minWidth: 0 }}>🗑</Button>
                </Stack>
              ))}
            </Stack>}

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        {/* 🚫 ПОРУШЕННЯ — спроби обміну контактами */}
        {violations.length > 0 && <Box sx={{ mb: 2, p: 1.4, borderRadius: 2, border: '1px solid rgba(255,70,70,.5)', bgcolor: 'rgba(255,60,60,.12)' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ color: '#ff8a8a', fontWeight: 700 }}>
              🚫 Спроби обміну контактами
              {violUnseen > 0 && <Chip label={violUnseen + ' нових'} size="small" sx={{ ml: 1, bgcolor: 'rgba(255,70,70,.3)', color: '#fff' }} />}
            </Typography>
            {violUnseen > 0 && <Button size="small" onClick={markViolationsSeen} sx={{ color: '#22d3ee' }}>Позначити переглянутими</Button>}
          </Stack>
          <Stack spacing={0.6}>
            {violations.slice(0, 30).map((v) => (
              <Box key={v.id} sx={{ p: 1, borderRadius: 1.5, bgcolor: v.seen ? 'rgba(28,24,52,.5)' : 'rgba(255,60,60,.16)' }}>
                <Typography sx={{ color: '#eee9ff', fontSize: 12 }}>
                  №{v.request_id} · {v.sender_role === 'consultant' ? '🧑‍🏫 ' + (v.consultant || 'консультант') : '👤 ' + v.user} · <span style={{ color: '#9a92c4' }}>{v.kind}</span>
                  {v.created_at ? <span style={{ color: '#9a92c4' }}> · {new Date(v.created_at).toLocaleString('uk-UA')}</span> : ''}
                </Typography>
                <Typography sx={{ color: '#ffb4b4', fontSize: 12, wordBreak: 'break-all' }}>{v.content}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>}

        {/* ВИПЛАТИ консультантам */}
        <Typography sx={{ color: '#c9c2e8', fontWeight: 700, mb: 1 }}>
          💰 Виплати консультантам
          {payouts.filter(p => p.payout_status === 'owed').length > 0 && <Chip label={payouts.filter(p => p.payout_status === 'owed').length + ' до виплати'} size="small" sx={{ ml: 1, bgcolor: 'rgba(57,229,140,.2)', color: '#39e58c' }} />}
        </Typography>
        {payouts.length === 0
          ? <Typography sx={{ color: '#9a92c4', mb: 2 }}>Оплачених консультацій ще немає. Тут з'являться суми до виплати консультантам (мінус твоя комісія).</Typography>
          : <Stack spacing={1} sx={{ mb: 2 }}>
              {payouts.map((p) => (
                <Box key={p.id} sx={{ p: 1.4, borderRadius: 2, border: '1px solid ' + (p.payout_status === 'paid' ? 'rgba(57,229,140,.4)' : 'rgba(255,70,70,.5)'), bgcolor: p.payout_status === 'paid' ? 'rgba(57,229,140,.08)' : 'rgba(255,60,60,.13)' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: '#eee9ff', fontSize: 14, fontWeight: 600 }}>№{p.id} · {p.consultant || '—'}{p.consultant_username ? ' @' + p.consultant_username : ''}</Typography>
                      <Typography sx={{ color: '#9a92c4', fontSize: 12 }}>Клієнт: {p.user}</Typography>
                      <Typography sx={{ color: '#c9c2e8', fontSize: 13, mt: 0.5 }}>
                        Отримано: <b style={{ color: '#ffd479' }}>{p.gross} {p.currency}</b> · комісія {p.commission}% ·
                        <b style={{ color: '#39e58c' }}> до виплати: {p.net != null ? p.net : '?'} {p.currency}</b>
                      </Typography>
                      <Typography sx={{ color: '#22d3ee', fontSize: 12, mt: 0.3, wordBreak: 'break-all' }}>💳 {p.payout_details || 'реквізити не вказані'}</Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.6 }}>
                        <Chip label={p.conducted ? '✅ проведено' : '⏳ очікує проведення'} size="small"
                          sx={{ bgcolor: p.conducted ? 'rgba(57,229,140,.15)' : 'rgba(255,212,121,.15)', color: p.conducted ? '#39e58c' : '#ffd479', fontSize: 10 }} />
                        {p.rating != null && <Chip label={'⭐ ' + p.rating + '/10'} size="small" sx={{ bgcolor: 'rgba(255,212,121,.15)', color: '#ffd479', fontSize: 10 }} />}
                      </Stack>
                      {p.review && <Typography sx={{ color: '#9a92c4', fontSize: 11, mt: 0.3, fontStyle: 'italic' }}>«{p.review}»</Typography>}
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                      {p.payout_status === 'paid'
                        ? <><Chip label="✅ виплачено" size="small" sx={{ bgcolor: 'rgba(57,229,140,.15)', color: '#39e58c', fontSize: 11 }} />
                            <Button size="small" onClick={() => markPayout(p.id, false)} sx={{ color: '#9a92c4', display: 'block', mt: 0.5, fontSize: 10 }}>↩ повернути</Button></>
                        : (p.conducted
                            ? <Button size="small" variant="outlined" onClick={() => markPayout(p.id, true)}
                                sx={{ borderColor: '#ff8a8a', color: '#ff8a8a', fontWeight: 700 }}>Не виплачено</Button>
                            : <Chip label="🔒 після консультації" size="small" sx={{ bgcolor: 'rgba(150,130,255,.12)', color: '#9a92c4', fontSize: 10 }} />)}
                      <Button size="small" onClick={() => deletePayout(p.id)} sx={{ color: '#ff6b6b', display: 'block', mt: 0.5, fontSize: 14, minWidth: 0 }} title="Прибрати">🗑</Button>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>}

        <Divider sx={{ borderColor: 'rgba(150,130,255,.12)', my: 2 }} />

        {/* Черга заявок */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ color: '#c9c2e8', fontWeight: 700 }}>
            Заявки на консультацію
            {consPending > 0 && <Chip label={consPending + ' нових'} size="small" sx={{ ml: 1, bgcolor: 'rgba(255,212,121,.2)', color: '#ffd479' }} />}
          </Typography>
          <Button size="small" onClick={loadConsult} sx={{ color: '#22d3ee' }}>↻ Оновити</Button>
        </Stack>
        {consReqs.length === 0
          ? <Typography sx={{ color: '#9a92c4' }}>Заявок ще немає.</Typography>
          : <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  {['#', 'Юзер', 'Астролог', 'Знак / народження', 'Пакет', 'Статус', 'Дата', 'Дії'].map(h =>
                    <TableCell key={h} sx={{ color: '#9a92c4', borderColor: 'rgba(150,130,255,.12)', fontSize: 11, textTransform: 'uppercase' }}>{h}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {consReqs.map((r) => {
                    const sc = STATUS_CHIP[r.status] || [r.status, 'rgba(124,92,255,.15)', '#cbb8ff'];
                    return (
                      <TableRow key={r.id} hover sx={r.status === 'pending' ? { bgcolor: 'rgba(255,60,60,.14)' } : {}}>
                        <TableCell sx={{ color: '#9a92c4', borderColor: 'rgba(150,130,255,.08)' }}>{r.id}</TableCell>
                        <TableCell sx={{ color: '#eee9ff', borderColor: 'rgba(150,130,255,.08)', fontSize: 13 }}>
                          {r.first_name || '—'}{r.username ? ' @' + r.username : ''}<br />
                          <span style={{ color: '#9a92c4', fontSize: 11 }}>{r.telegram_id}</span>
                        </TableCell>
                        <TableCell sx={{ color: '#c9c2e8', borderColor: 'rgba(150,130,255,.08)', fontSize: 13 }}>{r.consultant || '—'}</TableCell>
                        <TableCell sx={{ color: '#c9c2e8', borderColor: 'rgba(150,130,255,.08)', fontSize: 12 }}>
                          {r.sign || '—'}{r.birth_date ? <><br /><span style={{ color: '#9a92c4' }}>{r.birth_date}{r.birth_time ? ' ' + r.birth_time : ''}{r.birth_place ? ', ' + r.birth_place : ''}</span></> : ''}
                        </TableCell>
                        <TableCell sx={{ color: '#c9c2e8', borderColor: 'rgba(150,130,255,.08)', fontSize: 12 }}>
                          {r.package || '—'}</TableCell>
                        <TableCell sx={{ borderColor: 'rgba(150,130,255,.08)' }}>
                          <Chip label={sc[0]} size="small" sx={{ bgcolor: sc[1], color: sc[2], fontSize: 11 }} />
                          {r.scheduled_text && <div style={{ color: '#22d3ee', fontSize: 11, marginTop: 3 }}>📅 {r.scheduled_text}</div>}
                          {r.chat_open && <div style={{ color: '#cbb8ff', fontSize: 11 }}>💬 чат відкрито</div>}
                          {r.conducted && <div style={{ color: '#39e58c', fontSize: 11 }}>✅ проведено</div>}
                          {r.rating != null && <div style={{ color: '#ffd479', fontSize: 11 }}>⭐ {r.rating}/10</div>}
                        </TableCell>
                        <TableCell sx={{ color: '#9a92c4', borderColor: 'rgba(150,130,255,.08)', fontSize: 12 }}>
                          {r.created_at ? new Date(r.created_at).toLocaleString('uk-UA') : ''}</TableCell>
                        <TableCell sx={{ borderColor: 'rgba(150,130,255,.08)' }}>
                          <Stack direction="row" spacing={.5}>
                            {r.payment_id && <Button size="small" onClick={() => viewConsReceipt(r.id)} sx={{ color: '#22d3ee', minWidth: 0 }}>🧾</Button>}
                            {r.status === 'pending' && <>
                              <Button size="small" onClick={() => decideConsult(r.id, true)} disabled={consBusy} sx={{ color: '#39e58c', minWidth: 0 }}>✔</Button>
                              <Button size="small" onClick={() => decideConsult(r.id, false)} disabled={consBusy} sx={{ color: '#ff6b6b', minWidth: 0 }}>✖</Button>
                            </>}
                            <Button size="small" onClick={() => deleteConsultReq(r.id)} disabled={consBusy} sx={{ color: '#ff6b6b', minWidth: 0 }}>🗑</Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>}
        {consMsg && <Typography sx={{ color: consMsg.startsWith('✅') ? '#39e58c' : '#ff6b6b', fontSize: 14, mt: 1.5 }}>{consMsg}</Typography>}
      </Paper>

      {/* Форма додавання/редагування астролога */}
      <Dialog open={!!consForm} onClose={() => setConsForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#140f24', color: '#eee9ff' }}>{consForm && consForm.id ? 'Редагувати астролога' : 'Новий астролог'}</DialogTitle>
        <DialogContent sx={{ bgcolor: '#140f24', pt: 2 }}>
          {consForm && <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Ім'я" size="small" fullWidth value={consForm.name || ''}
              onChange={(e) => setCF('name', e.target.value)}
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            <TextField label="Спеціалізація (напр. натальна карта, таро)" size="small" fullWidth value={consForm.spec || ''}
              onChange={(e) => setCF('spec', e.target.value)}
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            <Stack direction="row" spacing={1.5}>
              <TextField label="Стаж (напр. 5 років)" size="small" fullWidth value={consForm.experience || ''}
                onChange={(e) => setCF('experience', e.target.value)}
                InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
              <TextField label="Років" type="number" size="small" sx={{ width: 90 }} value={consForm.age || ''}
                onChange={(e) => setCF('age', e.target.value)}
                InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <TextField label="Місто" size="small" fullWidth value={consForm.city || ''}
                onChange={(e) => setCF('city', e.target.value)}
                InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
              <TextField label="Країна" size="small" fullWidth value={consForm.country || ''}
                onChange={(e) => setCF('country', e.target.value)}
                InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            </Stack>
            <TextField label="Телефон (лише для тебе, юзерам не видно)" size="small" fullWidth value={consForm.phone || ''}
              onChange={(e) => setCF('phone', e.target.value)}
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            <TextField label="Як хоче працювати" size="small" fullWidth multiline value={consForm.work_pref || ''}
              onChange={(e) => setCF('work_pref', e.target.value)}
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            <TextField label="🗣 Мови консультацій (напр. українська, російська) — юзери бачать" size="small" fullWidth value={consForm.languages || ''}
              onChange={(e) => setCF('languages', e.target.value)}
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            <TextField label="💳 Реквізити консультанта для виплат (куди перераховувати його частину)" size="small" fullWidth multiline value={consForm.payout_details || ''}
              onChange={(e) => setCF('payout_details', e.target.value)}
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            {consForm.id && <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: 'rgba(124,92,255,.08)' }}>
              <Typography sx={{ color: '#c9c2e8', fontWeight: 700, fontSize: 13, mb: 1 }}>📦 Пакети (тривалість + ціна + валюта):</Typography>
              {editPkgs.length === 0 ? <Typography sx={{ color: '#9a92c4', fontSize: 12, mb: 1 }}>Ще немає пакетів.</Typography>
                : <Stack spacing={0.7} sx={{ mb: 1 }}>
                    {editPkgs.map((p) => (
                      <Stack key={p.id} direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ color: '#eee9ff', fontSize: 13, flex: 1 }}>⏱ {p.minutes} хв · <span style={{ color: '#ffd479' }}>{p.price} {p.currency}</span>{!p.active && ' (прих.)'}</Typography>
                        <Button size="small" onClick={() => setPkgRow({ ...p })} sx={{ color: '#22d3ee', minWidth: 0 }}>✎</Button>
                        <Button size="small" onClick={() => delEditPkg(p.id, consForm.id)} sx={{ color: '#ff6b6b', minWidth: 0 }}>🗑</Button>
                      </Stack>
                    ))}
                  </Stack>}
              {pkgRow && <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                <TextField label="Хв" type="number" size="small" sx={{ width: 70 }} value={pkgRow.minutes || ''} onChange={(e) => setPkgRow({ ...pkgRow, minutes: e.target.value })} InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
                <TextField label="Ціна" size="small" sx={{ width: 90 }} value={pkgRow.price || ''} onChange={(e) => setPkgRow({ ...pkgRow, price: e.target.value })} InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
                <TextField select SelectProps={{ native: true }} size="small" sx={{ width: 90 }} value={pkgRow.currency || 'USD'} onChange={(e) => setPkgRow({ ...pkgRow, currency: e.target.value })} InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }}>
                  {CURRENCIES.map((x) => <option key={x} value={x}>{x}</option>)}
                </TextField>
                <Button size="small" variant="contained" onClick={() => saveEditPkg(consForm.id)} sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f' }}>OK</Button>
                <Button size="small" onClick={() => setPkgRow(null)} sx={{ color: '#9a92c4' }}>✕</Button>
              </Stack>}
              <Button size="small" onClick={() => setPkgRow({ minutes: 30, price: '', currency: 'USD', active: true })} sx={{ color: '#22d3ee' }}>＋ Додати пакет</Button>
            </Box>}
            {!consForm.id && <Typography sx={{ color: '#9a92c4', fontSize: 12 }}>Пакети можна додати після збереження картки.</Typography>}
            <TextField label="Telegram @username консультанта (щоб бот пінгнув саме його в гілці)" size="small" fullWidth value={consForm.tg_username || ''}
              onChange={(e) => setCF('tg_username', e.target.value)} placeholder="maria_astro"
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            <TextField label="Telegram ID (для входу в кабінет; авто-заповниться, коли консультант напише боту /start)" size="small" fullWidth value={consForm.tg_id || ''}
              onChange={(e) => setCF('tg_id', e.target.value)} placeholder="напр. 1011013232"
              helperText={consForm.tg_id ? '✅ кабінет активний' : 'ще не активовано'}
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} FormHelperTextProps={{ sx: { color: consForm.tg_id ? '#39e58c' : '#9a92c4' } }} />
            <TextField label="Опис / біо" size="small" fullWidth multiline minRows={2} value={consForm.bio || ''}
              onChange={(e) => setCF('bio', e.target.value)}
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <Button component="label" size="small" sx={{ color: '#22d3ee' }}>
                📷 Фото{consPhoto ? ' ✓' : ''}
                <input type="file" accept="image/*" hidden onChange={(e) => setConsPhoto(e.target.files && e.target.files[0])} />
              </Button>
              {consForm.id && consForm.has_photo && !consPhoto &&
                <Avatar src={'/api/dashboard/zodiaq/consultant-photo/' + consForm.id} sx={{ width: 40, height: 40 }} />}
              <FormControlLabel control={<Switch checked={!!consForm.active} onChange={(e) => setCF('active', e.target.checked)} size="small" />}
                label={<Typography sx={{ color: '#c9c2e8', fontSize: 13 }}>Показувати юзерам</Typography>} />
            </Stack>
          </Stack>}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#140f24' }}>
          <Button onClick={() => setConsForm(null)} sx={{ color: '#9a92c4' }}>Скасувати</Button>
          <Button onClick={saveConsult} disabled={consBusy} variant="contained"
            sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>Зберегти</Button>
        </DialogActions>
      </Dialog>

      {/* Деталі заявки стати консультантом */}
      <Dialog open={!!appView} onClose={() => setAppView(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#140f24', color: '#eee9ff' }}>Заявка #{appView && appView.id}</DialogTitle>
        <DialogContent sx={{ bgcolor: '#140f24' }}>
          {appView && <Stack spacing={1} sx={{ mt: 1 }}>
            {appView.has_photo && <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Button size="small" onClick={() => viewAppPhoto(appView.id)} sx={{ color: '#22d3ee' }}>📷 Показати фото</Button>
            </Box>}
            {[['Ім\'я', (appView.first_name || '') + ' ' + (appView.last_name || '')], ['Username', appView.username ? '@' + appView.username : '—'],
              ['Telegram ID', appView.telegram_id], ['Сфера', appView.sphere], ['Стаж', appView.experience],
              ['Місто', appView.city], ['Країна', appView.country], ['Років', appView.age], ['Телефон', appView.phone],
              ['🗣 Мови', appView.languages], ['Як хоче працювати', appView.work_pref], ['💳 Реквізити для виплат', appView.payout_details], ['Додатково', appView.extra]].map(([k, v]) => v ? (
              <Stack key={k} direction="row" spacing={1}>
                <Typography sx={{ color: '#9a92c4', fontSize: 13, minWidth: 130 }}>{k}:</Typography>
                <Typography sx={{ color: '#eee9ff', fontSize: 13 }}>{v}</Typography>
              </Stack>) : null)}
            {appView.packages && (() => { let ps = []; try { ps = JSON.parse(appView.packages); } catch (e) { /* тихо */ } return ps.length ? (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ color: '#9a92c4', fontSize: 13, mb: 0.5 }}>Пакети:</Typography>
                {ps.map((p, i) => <Typography key={i} sx={{ color: '#ffd479', fontSize: 13 }}>⏱ {p.minutes} хв · {p.price} {p.currency}</Typography>)}
              </Box>) : null; })()}
          </Stack>}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#140f24' }}>
          <Button onClick={() => appView && deleteApp(appView.id)} disabled={consBusy} sx={{ color: '#ff6b6b', mr: 'auto' }}>🗑 Видалити</Button>
          <Button onClick={() => setAppView(null)} sx={{ color: '#9a92c4' }}>Закрити</Button>
          {appView && appView.status === 'pending' && <>
            <Button onClick={() => decideApp(appView.id, false, window.prompt('Причина відмови:') || '')} disabled={consBusy} sx={{ color: '#ff6b6b' }}>Відхилити</Button>
            <Button onClick={() => decideApp(appView.id, true)} disabled={consBusy} variant="contained"
              sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>Прийняти</Button>
          </>}
        </DialogActions>
      </Dialog>

      {/* Фото заявки */}
      <Dialog open={!!appPhoto} onClose={() => { if (appPhoto) URL.revokeObjectURL(appPhoto); setAppPhoto(null); }} maxWidth="sm" fullWidth>
        <DialogContent sx={{ bgcolor: '#140f24', textAlign: 'center' }}>
          {appPhoto && <img src={appPhoto} alt="фото заявки" style={{ maxWidth: '100%', borderRadius: 8 }} />}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#140f24' }}>
          <Button onClick={() => { if (appPhoto) URL.revokeObjectURL(appPhoto); setAppPhoto(null); }} sx={{ color: '#22d3ee' }}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* Перегляд чека консультації */}
      <Dialog open={!!consReceipt} onClose={() => { if (consReceipt) URL.revokeObjectURL(consReceipt.url); setConsReceipt(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#140f24', color: '#eee9ff' }}>Чек заявки #{consReceipt && consReceipt.rid}</DialogTitle>
        <DialogContent sx={{ bgcolor: '#140f24', textAlign: 'center' }}>
          {consReceipt && <img src={consReceipt.url} alt="чек" style={{ maxWidth: '100%', borderRadius: 8 }} />}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#140f24' }}>
          <Button onClick={() => { if (consReceipt) URL.revokeObjectURL(consReceipt.url); setConsReceipt(null); }} sx={{ color: '#22d3ee' }}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ ПІДТРИМКА (чат з автоперекладом) ═══ */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s12'), position: 'relative', display: _zqVis('s12') }} {..._zqDP('s12')}>{_zqGrip('s12')}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#eee9ff' }}>
            🆘 Підтримка
            {supUnread > 0 && <Chip label={supUnread + ' нових'} size="small" sx={{ ml: 1, bgcolor: 'rgba(255,107,107,.2)', color: '#ff6b6b' }} />}
          </Typography>
          <Button size="small" onClick={loadThreads} sx={{ color: '#22d3ee' }}>↻ Оновити</Button>
        </Stack>
        <Typography sx={{ color: '#9a92c4', mb: 2, fontSize: 13 }}>
          Юзер пише своєю мовою — тобі показуємо українською. Твоя відповідь автоматично перекладається на його мову й іде в бот.
        </Typography>
        {threads.length === 0
          ? <Typography sx={{ color: '#9a92c4' }}>Звернень ще немає.</Typography>
          : (
            <Stack spacing={1}>
              {threads.map((t) => (
                <Stack key={t.telegram_id} direction="row" spacing={2} alignItems="center" onClick={() => openThread(t.telegram_id)}
                  sx={{ cursor: 'pointer', bgcolor: t.unread ? 'rgba(255,107,107,.08)' : 'rgba(28,24,52,.5)', border: '1px solid rgba(150,130,255,.12)', borderRadius: 2, px: 2, py: 1, '&:hover': { borderColor: 'rgba(150,130,255,.35)' } }}>
                  <Typography sx={{ color: '#eee9ff', flex: 1, fontSize: 14 }}>
                    {t.first_name || '—'}{t.username ? ' @' + t.username : ''} <span style={{ color: '#9a92c4', fontSize: 12 }}>· {t.telegram_id}</span>
                  </Typography>
                  {t.unread > 0 && <Chip label={t.unread} size="small" sx={{ bgcolor: 'rgba(255,107,107,.2)', color: '#ff6b6b', fontSize: 11 }} />}
                  <Typography sx={{ color: '#9a92c4', fontSize: 12 }}>{t.last_at ? new Date(t.last_at).toLocaleString('uk-UA') : ''}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
      </Paper>

      {/* Діалог гілки підтримки */}
      <Dialog open={!!supTid} onClose={() => setSupTid(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#140f24', color: '#eee9ff' }}>🆘 Переписка · {supTid}</DialogTitle>
        <DialogContent sx={{ bgcolor: '#140f24' }}>
          <Stack spacing={1} sx={{ mt: 1, mb: 2 }}>
            {supMsgs.length === 0 ? <Typography sx={{ color: '#9a92c4' }}>Порожньо.</Typography>
              : supMsgs.map((m, i) => (
                <Box key={i} sx={{ alignSelf: m.dir === 'out' ? 'flex-end' : 'flex-start', maxWidth: '80%',
                  bgcolor: m.dir === 'out' ? 'rgba(34,211,238,.12)' : 'rgba(124,92,255,.14)',
                  border: '1px solid rgba(150,130,255,.15)', borderRadius: 2, px: 1.5, py: 1 }}>
                  <Typography sx={{ color: '#eee9ff', fontSize: 14 }}>{m.text}</Typography>
                  <Typography sx={{ color: '#9a92c4', fontSize: 10, mt: .3 }}>
                    {m.dir === 'out' ? 'ти' : ('юзер · ' + (m.lang || ''))}{m.at ? ' · ' + new Date(m.at).toLocaleString('uk-UA') : ''}
                  </Typography>
                </Box>
              ))}
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField fullWidth size="small" placeholder="Відповідь українською…" value={supReply}
              onChange={(e) => setSupReply(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
              InputLabelProps={{ sx: { color: '#9a92c4' } }} inputProps={{ style: { color: '#eee9ff' } }} />
            <Button variant="contained" onClick={sendReply} disabled={supBusy || !supReply.trim()}
              sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>➤</Button>
          </Stack>
          {supDeliver
            ? <Typography sx={{ color: supDeliver.startsWith('✅') ? '#39e58c' : '#ffb454', fontSize: 12, mt: 1 }}>{supDeliver}</Typography>
            : <Typography sx={{ color: '#9a92c4', fontSize: 11, mt: 1 }}>Відповідь автоперекладеться на мову юзера й піде йому в бот.</Typography>}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#140f24' }}>
          <Button onClick={() => setSupTid(null)} sx={{ color: '#22d3ee' }}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* Список юзерів */}
      <Paper sx={{ ...paper, order: _zqOrd.indexOf('s13'), position: 'relative', display: _zqVis('s13') }} {..._zqDP('s13')}>{_zqGrip('s13')}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#eee9ff' }}>
            👥 Юзери <Chip label={count} size="small" sx={{ ml: 1, bgcolor: 'rgba(124,92,255,.2)', color: '#fff' }} />
            {newCount > 0 && <Chip label={'🔴 ' + newCount + ' нових'} size="small" sx={{ ml: 1, bgcolor: 'rgba(255,107,107,.2)', color: '#ff6b6b' }} />}
          </Typography>
          <Stack direction="row" spacing={1}>
            {newCount > 0 && <Button size="small" onClick={() => markSeen(null, true)} sx={{ color: '#9a92c4' }}>Позначити всі</Button>}
            <Button size="small" onClick={load} sx={{ color: '#22d3ee' }}>↻ Оновити</Button>
          </Stack>
        </Stack>
        {/* 💎 2026-08-14 (OWNER): КОРИСТУВАННЯ, а не доступ — «🔥 Активні» (візит за 7 днів)
            і «😴 Тихі» (зайшов і зник). Рахується з полів, які API вже віддає. */}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
          {[['all', 'Всі'], ['active7', '🔥 Активні (7д)'], ['quiet', '😴 Тихі']].map(([k, lbl]) => {
            const _n = k === 'all' ? users.length
              : k === 'active7' ? users.filter(_zqIsActive7).length
              : users.filter(_zqIsQuiet).length;
            return (
              <Chip key={k} label={`${lbl} · ${_n}`} onClick={() => setUFilter(k)} size="small"
                sx={{ cursor: 'pointer', fontWeight: uFilter === k ? 700 : 400,
                      bgcolor: uFilter === k ? 'rgba(124,92,255,.45)' : 'rgba(255,255,255,.06)',
                      color: uFilter === k ? '#fff' : '#9a92c4' }} />
            );
          })}
        </Stack>
        {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          : err ? <Alert severity="error">{err}</Alert>
          : users.length === 0 ? <Typography sx={{ color: '#9a92c4' }}>Поки що ніхто не відкрив додаток. Список наповниться, щойно юзери зайдуть.</Typography>
          : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['#', 'Ім\'я', 'Username', 'Днів', 'ДН', '💎 Токени', 'Преміум', 'Telegram ID'].map(h =>
                      <TableCell key={h} sx={{ color: '#9a92c4', borderColor: 'rgba(150,130,255,.12)', fontSize: 11, textTransform: 'uppercase' }}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.filter(u => uFilter === 'active7' ? _zqIsActive7(u)
                                    : uFilter === 'quiet' ? _zqIsQuiet(u) : true).map((u, i) => (
                    <TableRow key={u.telegram_id} hover onClick={() => { setUserDetail(u); if (u.is_new) markSeen(u.telegram_id); }}
                      sx={{ cursor: 'pointer', bgcolor: u.is_new ? 'rgba(255,107,107,.10)' : 'transparent', '&:hover': { bgcolor: u.is_new ? 'rgba(255,107,107,.16)' : 'rgba(124,92,255,.06)' } }}>
                      <TableCell sx={{ color: '#9a92c4', borderColor: 'rgba(150,130,255,.08)' }}>{u.is_new ? '🔴' : (i + 1)}</TableCell>
                      <TableCell sx={{ color: u.is_new ? '#ff9a9a' : '#eee9ff', borderColor: 'rgba(150,130,255,.08)', fontWeight: u.is_new ? 700 : 400 }}>{u.first_name || '—'}</TableCell>
                      <TableCell sx={{ color: '#22d3ee', borderColor: 'rgba(150,130,255,.08)' }}>{u.username ? '@' + u.username : '—'}</TableCell>
                      <TableCell sx={{ color: '#c9c2e8', borderColor: 'rgba(150,130,255,.08)', fontSize: 13 }}>{u.active_days != null ? u.active_days : '—'}</TableCell>
                      <TableCell sx={{ color: '#c9c2e8', borderColor: 'rgba(150,130,255,.08)', fontSize: 13 }}>{u.sign ? u.sign.glyph : (u.birth_date ? '•' : '—')}</TableCell>
                      {/* 💎 14.09.2026: скільки Є і скільки ВИТРАЧЕНО. Один баланс не
                          показує, чи людина взагалі користується токенами. */}
                      <TableCell sx={{ borderColor: 'rgba(150,130,255,.08)', whiteSpace: 'nowrap' }}>
                        <span style={{ color: (u.credits > 0 ? '#ffd479' : '#6a6390'), fontSize: 13, fontWeight: 600 }}>
                          {u.credits != null ? u.credits : '—'}
                        </span>
                        {u.spent > 0 && (
                          <span style={{ color: '#9a92c4', fontSize: 11, marginLeft: 6 }}>
                            −{u.spent}{u.earned > 0 ? ' з ' + u.earned : ''}
                          </span>
                        )}
                      </TableCell>
                      <TableCell sx={{ borderColor: 'rgba(150,130,255,.08)' }}>
                        {u.premium
                          ? <span style={{ color: '#ffd479', fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>💎 {fmtCountdown(u.premium_until)}</span>
                          : <span style={{ color: '#6a6390', fontSize: 13 }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ color: '#9a92c4', borderColor: 'rgba(150,130,255,.08)', fontSize: 13 }}>{u.telegram_id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </Paper>

      {/* Картка юзера */}
      <Dialog open={!!userDetail} onClose={() => setUserDetail(null)} maxWidth="xs" fullWidth>
        {userDetail && (<>
          <DialogTitle sx={{ bgcolor: '#140f24', color: '#eee9ff' }}>
            {userDetail.first_name || '—'}{userDetail.username ? ' · @' + userDetail.username : ''}
          </DialogTitle>
          <DialogContent sx={{ bgcolor: '#140f24' }}>
            <Stack spacing={1.2} sx={{ mt: 1 }}>
              {[
                ['🆔 Telegram ID', userDetail.telegram_id],
                ['🌐 Мова', userDetail.lang || '—'],
                ['📅 Перший вхід', userDetail.first_seen ? new Date(userDetail.first_seen).toLocaleString('uk-UA') : '—'],
                ['📅 Активних днів', userDetail.active_days != null ? userDetail.active_days + ' дн.' : '—'],
                ['🔥 Днів поспіль', userDetail.streak ? userDetail.streak + (userDetail.longest ? ' (рекорд ' + userDetail.longest + ')' : '') : '—'],
                ['🎂 День народження', userDetail.birth_date ? (fmtBirthday(userDetail.birth_date) + (userDetail.sign ? ' · ' + userDetail.sign.glyph + ' ' + userDetail.sign.name : '')) : 'не вказано'],
                ['📍 Місто народження', userDetail.birth_place || '—'],
                ['🕐 Останній візит', userDetail.last_visit || '—'],
                ['🪙 Токенів', userDetail.credits != null ? String(userDetail.credits) : '—'],
                ['🎁 Запросив друзів', userDetail.invited_count != null ? String(userDetail.invited_count) : '0'],
                ['🔗 Прийшов від', userDetail.referred_by_name || '—'],
              ].map(([k, v]) => (
                <Stack key={k} direction="row" justifyContent="space-between" sx={{ borderBottom: '1px solid rgba(150,130,255,.08)', pb: .8 }}>
                  <Typography sx={{ color: '#9a92c4', fontSize: 14 }}>{k}</Typography>
                  <Typography sx={{ color: '#eee9ff', fontSize: 14, textAlign: 'right' }}>{v}</Typography>
                </Stack>
              ))}
              {/* per-user захист від скріна */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ borderBottom: '1px solid rgba(150,130,255,.08)', pb: .3 }}>
                <Typography sx={{ color: '#9a92c4', fontSize: 14 }}>🛡 Захист від скріна</Typography>
                <Switch size="small" checked={userDetail.screen_protection !== false}
                  onChange={(e) => toggleUserProtect(userDetail.telegram_id, e.target.checked)} color="warning" />
              </Stack>
              <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: userDetail.premium ? 'rgba(255,212,121,.1)' : 'rgba(124,92,255,.08)', border: '1px solid ' + (userDetail.premium ? 'rgba(255,212,121,.3)' : 'rgba(150,130,255,.15)') }}>
                {userDetail.premium ? (<>
                  <Typography sx={{ color: '#ffd479', fontWeight: 700, fontSize: 14 }}>💎 Преміум активний</Typography>
                  <Typography sx={{ color: '#c9c2e8', fontSize: 13, mt: .5 }}>
                    діє до {userDetail.premium_until ? new Date(userDetail.premium_until).toLocaleDateString('uk-UA') : '—'}
                  </Typography>
                  <Typography sx={{ color: '#39e58c', fontSize: 18, fontWeight: 800, fontFamily: 'monospace', mt: .5 }}>
                    {fmtCountdown(userDetail.premium_until)}
                  </Typography>
                </>) : (
                  <Typography sx={{ color: '#9a92c4', fontSize: 14 }}>Без преміуму</Typography>
                )}
              </Box>
              {/* Ручна видача преміуму */}
              <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: 'rgba(124,92,255,.08)', border: '1px solid rgba(150,130,255,.15)' }}>
                <Typography sx={{ color: '#c9c2e8', fontWeight: 700, fontSize: 14, mb: 1 }}>🎁 Видати преміум вручну</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  {[1, 3, 6, 12].map(m => (
                    <Chip key={m} label={m + ' міс'} size="small" clickable onClick={() => setGrantMonths(m)}
                      sx={{ bgcolor: grantMonths === m ? 'rgba(255,212,121,.25)' : 'rgba(124,92,255,.15)',
                        color: grantMonths === m ? '#ffd479' : '#cbb8ff', fontWeight: grantMonths === m ? 700 : 400 }} />
                  ))}
                  <Button size="small" variant="contained" onClick={() => grantPremium(userDetail.telegram_id, grantMonths)}
                    sx={{ background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', fontWeight: 700 }}>
                    Видати {grantMonths} міс
                  </Button>
                  {userDetail.premium && <Button size="small" onClick={() => revokePremium(userDetail.telegram_id)} sx={{ color: '#ff6b6b' }}>Зняти</Button>}
                </Stack>
                {grantMsg && <Typography sx={{ color: grantMsg.startsWith('✅') ? '#39e58c' : '#ff6b6b', fontSize: 13, mt: 1 }}>{grantMsg}</Typography>}
              </Box>
              {/* 🎁 2026-09-11: подарунок токенів. Нарахування вміло працювати й раніше
                  (/credits/grant), але кнопки не було ніде — лишалось хіба руками в базі. */}
              <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: 'rgba(34,211,238,.07)', border: '1px solid rgba(34,211,238,.25)' }}>
                <Typography sx={{ color: '#c9c2e8', fontWeight: 700, fontSize: 14, mb: 1 }}>🪙 Подарувати токени</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {[5, 10, 25, 50].map(n => (
                    <Chip key={n} label={'+' + n} size="small" clickable onClick={() => setGiftAmount(String(n))}
                      sx={{ bgcolor: String(n) === String(giftAmount) ? 'rgba(34,211,238,.28)' : 'rgba(124,92,255,.15)',
                        color: String(n) === String(giftAmount) ? '#22d3ee' : '#cbb8ff',
                        fontWeight: String(n) === String(giftAmount) ? 700 : 400 }} />
                  ))}
                  <TextField size="small" type="number" value={giftAmount}
                    onChange={(e) => setGiftAmount(e.target.value)}
                    inputProps={{ min: 1, style: { color: '#eee9ff', width: 62, padding: '6px 8px' } }}
                    sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(150,130,255,.3)' } }} />
                  <Button size="small" variant="contained" disabled={giftBusy}
                    onClick={() => giftCredits(userDetail.telegram_id)}
                    sx={{ background: 'linear-gradient(120deg,#22d3ee,#a855f7)', color: '#07070f', fontWeight: 700 }}>
                    {giftBusy ? '…' : '🎁 Подарувати'}
                  </Button>
                </Stack>
                <FormControlLabel sx={{ mt: .5, ml: 0 }}
                  control={<Switch size="small" checked={giftNotify} onChange={(e) => setGiftNotify(e.target.checked)} color="info" />}
                  label={<Typography sx={{ color: '#9a92c4', fontSize: 12.5 }}>🎉 святкове вітання у бот (фото + ефект)</Typography>} />
                {giftMsg && <Typography sx={{ color: giftMsg.startsWith('✅') ? '#39e58c' : (giftMsg.startsWith('⚠️') ? '#ffd479' : '#ff6b6b'), fontSize: 13, mt: .5 }}>{giftMsg}</Typography>}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ bgcolor: '#140f24', justifyContent: 'space-between' }}>
            <Button onClick={() => { const tid = userDetail.telegram_id; setUserDetail(null); setGrantMsg(''); setGiftMsg(''); openThread(tid); }}
              sx={{ color: '#39e58c' }}>✍️ Написати юзеру</Button>
            <Button onClick={() => { setUserDetail(null); setGrantMsg(''); setGiftMsg(''); }} sx={{ color: '#22d3ee' }}>Закрити</Button>
          </DialogActions>
        </>)}
      </Dialog>
    </Container>
  );
}
